var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import '../styles/widgets/fractionTileManipulative.scss';
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import globalStore from '../store';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { getFractionDimensions, parseFraction, selectTool, bringManipulativesToFront, polarToCartesian, describeArc, getFractionCircleConfig, getCircleBoundingBox, getCircleClipPath, sumAndReduceFractions, getFractionAriaText, } from '../helper/helper';
import { Fraction } from '../components/MathJax';
import { getSnappedPosition, findTouchingParent, computeSnapRelation, solveDockMatchingScale, resolveSnappedChainGeometry, } from '../helper/snapHelper';
import { v4 as uuidv4 } from 'uuid';
import { snapTileToNumberLine } from '../helper/numberLineHelper';
import FractionTileMathEditor from './FractionTileMathEditor';
import ButtonComponent from '../components/ButtonComponent';
import SelectFocusOverlay from '../components/SelectFocusOverlay';
import { playAudio } from '../helper/audioHelper';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
import { FRACTION_CONFIG } from '../constants/fractionConfig';
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
/** Convert a hex color to rgba with transparency for highlighter-style shading.
 *  Default/white colors stay opaque so the tile background remains solid. */
var toShadeColor = function (hex, alpha) {
    if (alpha === void 0) { alpha = 0.4; }
    if (!hex)
        return '#fff';
    var lower = hex.toLowerCase();
    if (lower === '#fff' || lower === '#ffffff' || lower === 'white' || lower === '#ededed')
        return hex;
    var r = parseInt(hex.slice(1, 3), 16);
    var g = parseInt(hex.slice(3, 5), 16);
    var b = parseInt(hex.slice(5, 7), 16);
    if (isNaN(r) || isNaN(g) || isNaN(b))
        return hex;
    return "rgba(".concat(r, ", ").concat(g, ", ").concat(b, ", ").concat(alpha, ")");
};
var getLabelAriaText = function (label) {
    if (typeof label === 'string')
        return label;
    if (Array.isArray(label)) {
        return label
            .map(function (item) { return (item.type === 'math' ? item.latex || '' : item.value || ''); })
            .join(' ');
    }
    return '';
};
/** Max characters allowed in the custom label for each fraction size per shape. */
var FRACTION_MAX_CHARS = {
    rectangle: {
        'fraction-1': 32,
        'fraction-1-2': 16,
        'fraction-1-3': 10,
        'fraction-1-4': 8,
        'fraction-1-5': 6,
        'fraction-1-6': 5,
        'fraction-1-8': 3,
        'fraction-1-10': 2,
        'fraction-1-12': 2,
    },
    circle: {
        'fraction-1': 12,
        'fraction-1-2': 6,
        'fraction-1-3': 6,
        'fraction-1-4': 6,
        'fraction-1-5': 5,
        'fraction-1-6': 4,
        'fraction-1-8': 3,
        'fraction-1-10': 2,
        'fraction-1-12': 2,
    },
};
var FractionTileManipulative = function (props) {
    var _a, _b, _c;
    var id = props.id, center = props.center, dimensions = props.dimensions, scale = props.scale, _d = props.disabled, disabled = _d === void 0 ? false : _d, _e = props.lock, positionLock = _e.positioning, editingLock = _e.editing, shapeType = props.shapeType, lablesType = props.lablesType, customLabel = props.customLabel, fractionSize = props.fractionSize, color = props.color, customSweep = props.customSweep, customNumerator = props.customNumerator, customDenominator = props.customDenominator, rotation = props.rotation, gluedFrom = props.gluedFrom, defaultDimensions = props.defaultDimensions, groupId = props.groupId;
    /** ---------------- GLOBAL CONFIG ---------------- */
    var _f = globalStore.useStoreState(function (store) { return store.playerStore; }), zoomLevel = _f.zoomLevel, playerBgPattern = _f.playerBgPattern;
    var _g = globalStore.useStoreState(function (store) { return store.jsonDataStore; }), bottomToolConfig = _g.bottomToolConfig, btnConfig = _g.btnConfig;
    /** ---------------- DATA STORE FROM GAME CONTROLLER ---------------- */
    var _h = useContext(DataContext), selectedTool = _h.selectedTool, setSelectedTool = _h.setSelectedTool, setManipulative = _h.setManipulative, manipulative = _h.manipulative, setActiveManipulator = _h.setActiveManipulator, setManiplulatorComponentVisible = _h.setManiplulatorComponentVisible, setSelectedManipulativeId = _h.setSelectedManipulativeId, panningEnable = _h.panningEnable, setContextMenu = _h.setContextMenu;
    // Broadcasts this tile's drag as a multi-select leader so OTHER-type items (dice, tiles,
    // decks, ...) selected alongside it also follow. This widget's own multi-select move logic
    // below (in handleMove) already coordinates OTHER fractionTiles items directly via
    // initialSelectedPositions — it predates and doesn't use this hook — so fractionTiles is
    // excluded from the central engine's captured followers (see SELF_MANAGED_TYPES in
    // multiDragHelper.ts) to avoid a double-move; the follower effect further below covers a
    // fractionTile following a DIFFERENT-type leader instead.
    // Use the group token (when grouped) as the broadcast source id — grouped tiles carry
    // `manipulativeGroup_...` in `selectedTool`, not their own `id` (see `isFocused` above), so
    // keying this off bare `id` would never satisfy `selectedTool.includes(...)` and silently
    // skip the broadcast entirely whenever a fraction-tile group is the multi-select leader.
    var _j = useMultiSelectDragSource(groupId !== null && groupId !== void 0 ? groupId : id, selectedTool), startMultiDrag = _j.startMultiDrag, moveMultiDrag = _j.moveMultiDrag, endMultiDrag = _j.endMultiDrag;
    /** ---------------- DEFAULT TILE DIMENSION ---------------- */
    var defaultDimension = useMemo(function () {
        var _a;
        if (defaultDimensions)
            return defaultDimensions;
        var config = getFractionDimensions(fractionSize);
        return {
            width: ((_a = config === null || config === void 0 ? void 0 : config.rectangle) === null || _a === void 0 ? void 0 : _a.width) || 660,
            height: 55,
        };
    }, [fractionSize, defaultDimensions]);
    /** ---------------- STATE ---------------- */
    var _k = useState(center), tilePosition = _k[0], setTilePosition = _k[1];
    var isFocused = useMemo(function () {
        return selectedTool.some(function (toolId) { return toolId.startsWith(id); }) ||
            (!!groupId && selectedTool.includes(groupId));
    }, [selectedTool, id, groupId]);
    var _l = useState(null), dragStart = _l[0], setDragStart = _l[1];
    var _m = useState(scale), tileScale = _m[0], setTileScale = _m[1];
    var _o = useState(dimensions), tileSize = _o[0], setTileSize = _o[1];
    var _p = useState(null), snapPreview = _p[0], setSnapPreview = _p[1];
    var labelWrapperRef = useRef(null);
    var labelWrapperCircleRef = useRef(null);
    var mathEditorRef = useRef(null);
    var wasEditingAtPointerRef = useRef(false);
    var _q = useState(-1), mathKeyboardActive = _q[0], setMathKeyboardActive = _q[1];
    var _r = useState(false), isEditing = _r[0], setIsEditing = _r[1];
    var _s = useState(''), announceText = _s[0], setAnnounceText = _s[1];
    /** ---------------- REF ---------------- */
    var tileInteractiveRef = useRef(null);
    var isFocusedRef = useRef(false);
    var tilePosRef = useRef(tilePosition);
    var dragStartRef = useRef(null);
    var hasMoved = useRef(false);
    var dragStated = useRef(false);
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    var prevSelectedToolRef = useRef([]);
    // Flag set in onMouseDown/onTouchStart so onFocus knows not to override mouse-driven selection
    var pointerDownRef = useRef(false);
    // Timestamp of most recent touchstart on this tile — used to distinguish VoiceOver click
    // (no preceding touchstart) from regular iPad tap (touchstart < 500ms before click).
    var lastTouchStartTimeRef = useRef(0);
    // Set when onFocus fires WITHOUT a preceding pointer event (keyboard Tab or VoiceOver swipe).
    // Consumed (cleared) when used to bypass the double-tap requirement in onClick so that
    // residual pointer-driven isFocused state never shortcircuits the double-tap check.
    var voiceFocusRef = useRef(false);
    // Mirrors SpinnerManipulative: true when the tile received focus via a
    // screen reader (VoiceOver sets :focus-visible; regular touch does not).
    // Used to restrict iPad context-menu opening to VoiceOver activation only.
    var lastFocusWasScreenReader = useRef(false);
    // Set in onTouchEnd whenever the context menu is opened so the label wrapper's
    // onClick (which fires after touchend) does not activate the text editor.
    var suppressNextLabelClick = useRef(false);
    var manipulativeRef = useRef(manipulative);
    manipulativeRef.current = manipulative;
    var selectedToolRef = useRef(selectedTool);
    selectedToolRef.current = selectedTool;
    var tileSizeRef = useRef(tileSize);
    tileSizeRef.current = tileSize;
    // Captures this tile's scale/numberLineId/preSnapScale exactly as they were
    // before onMouseDown's eager preSnapScale restore (below) mutates them —
    // stopDrag's undo-entry diff needs the TRUE pre-interaction values, not the
    // already-restored ones, or a drag-away from a number line would produce no
    // undo entry at all (the restore already happened silently at mousedown).
    var preMouseDownNlStateRef = useRef(new Map());
    var zoomLevelRef = useRef(zoomLevel);
    zoomLevelRef.current = zoomLevel;
    /** ---------------- FOCUS MANAGEMENT ON SELECTION CHANGE (LIKE DICE) ---------------- */
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(id);
        var nowFocused = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        isFocusedRef.current = nowFocused;
        // Auto-focus only when this is the sole selected tile — if multiple tiles
        // are newly selected at once (e.g. after split), all of them would schedule
        // focus timers and race/cascade infinitely.
        if (nowFocused &&
            !wasFocused &&
            !disabled &&
            tileInteractiveRef.current &&
            selectedTool.length === 1) {
            setTimeout(function () {
                var _a, _b;
                // Don't steal focus if it is already somewhere inside this tile.
                // This covers the label input, math-fields inserted by insertMath(),
                // and any other interactive descendant.  Using tileInteractiveRef
                // (the outermost tile div) as the root is more reliable than checking
                // individual label wrappers: on iOS, document.activeElement for a
                // focused <math-field> web component may not be detected by a narrow
                // labelWrapper.contains() check, causing the 100 ms timeout to steal
                // focus from the math-field and close the MathLive keyboard.
                if ((_a = tileInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement))
                    return;
                // Don't steal focus if the user has since tabbed/selected elsewhere —
                // otherwise this stale timer yanks focus back onto this tile after a
                // fast Tab press has already moved on, producing a 2-way focus loop
                // between this tile and whatever the user tabbed to next.
                if (selectedToolRef.current.length !== 1 || selectedToolRef.current[0] !== id)
                    return;
                (_b = tileInteractiveRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }, 100);
        }
    }, [selectedTool, disabled, id]);
    /** ---------------- KEYBOARD NAVIGATION ---------------- */
    useEffect(function () {
        // Only listen for keydown when tile is focused to avoid global conflicts
        if (!isFocused)
            return;
        var handleKeyDown = function (e) {
            var _a, _b, _c;
            var target = e.target;
            // Guard: only act when DOM focus is actually on this tile's interactive element.
            // `isFocused` only means the tile is *selected* (in selectedTool), not that it has
            // real DOM focus. Without this check, keys pressed in the right-side panel or any
            // other UI while a tile is selected would wrongly move the tile / open its context menu.
            if (!((_a = tileInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)))
                return;
            if (target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.tagName === 'MATH-FIELD' ||
                target.tagName === 'BUTTON' ||
                target.isContentEditable) {
                return;
            }
            // Context menu: Enter, Space, Shift+F10, ContextMenu key (mirrors Dice pattern)
            if ((e.key === 'Enter' ||
                e.key === ' ' ||
                e.key === 'ContextMenu' ||
                (e.shiftKey && e.key === 'F10')) &&
                isFocused) {
                e.preventDefault();
                if (!selectedTool.includes("".concat(id))) {
                    setSelectedTool(["".concat(id)]);
                }
                var rect = (_b = tileInteractiveRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
                if (rect) {
                    setContextMenu({
                        x: rect.right,
                        y: rect.top,
                        manipulativeId: id,
                    });
                }
                return;
            }
            // Handle Delete separately - don't require position lock check
            if (e.key === 'Delete') {
                if (!isFocused || disabled || positionLock)
                    return;
                customEventHandler.dispatchEvent('bottomPanel', { id: 'delete', panel: 'bottomPanel' });
                e.preventDefault();
                return;
            }
            // Only handle arrow keys when this tile is focused and not position locked
            if (!isFocused || positionLock || disabled)
                return;
            var MOVE_INCREMENT = 5;
            var deltaX = 0;
            var deltaY = 0;
            switch (e.key) {
                case 'ArrowUp':
                    deltaY = -MOVE_INCREMENT;
                    e.preventDefault();
                    break;
                case 'ArrowDown':
                    deltaY = MOVE_INCREMENT;
                    e.preventDefault();
                    break;
                case 'ArrowLeft':
                    deltaX = -MOVE_INCREMENT;
                    e.preventDefault();
                    break;
                case 'ArrowRight':
                    deltaX = MOVE_INCREMENT;
                    e.preventDefault();
                    break;
                default:
                    // Prevent arrow keys from scrolling the stage even when movement is locked
                    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        e.preventDefault();
                    }
                    return;
            }
            // Track movement for announcement (like Dice)
            var curPos = (_c = keyMoveCurPosRef.current) !== null && _c !== void 0 ? _c : tilePosition;
            if (!keyMoveStartRef.current) {
                keyMoveStartRef.current = __assign({}, curPos);
            }
            // Calculate new position
            var newX = curPos.x + deltaX;
            var newY = curPos.y + deltaY;
            var newPosition = { x: newX, y: newY };
            keyMoveCurPosRef.current = newPosition;
            // Update the position
            setTilePosition(newPosition);
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newPosition }) }) : item;
                });
            });
            // Debounced save + screen reader announcement (like Dice pattern)
            if (keyMoveSaveTimer.current)
                clearTimeout(keyMoveSaveTimer.current);
            keyMoveSaveTimer.current = setTimeout(function () {
                if (keyMoveStartRef.current && keyMoveCurPosRef.current) {
                    var oldPos = keyMoveStartRef.current;
                    var newPos_1 = keyMoveCurPosRef.current;
                    saveState({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(oldPos),
                        new: JSON.stringify(newPos_1),
                    });
                    var dx = Math.round(newPos_1.x - oldPos.x);
                    var dy = Math.round(newPos_1.y - oldPos.y);
                    var directions_1 = [];
                    if (dy < 0)
                        directions_1.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                    if (dy > 0)
                        directions_1.push("".concat(t('text.down'), " ").concat(dy));
                    if (dx < 0)
                        directions_1.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                    if (dx > 0)
                        directions_1.push("".concat(t('text.right'), " ").concat(dx));
                    setAnnounceText('');
                    requestAnimationFrame(function () {
                        setAnnounceText(t('text.manipulativeMoved', {
                            name: t('text.manipulativeFractionTile'),
                            direction: directions_1.join(" ".concat(t('text.and'), " ")),
                            x: Math.round(newPos_1.x),
                            y: Math.round(newPos_1.y),
                        }));
                    });
                    keyMoveStartRef.current = null;
                    keyMoveCurPosRef.current = null;
                }
            }, 500);
        };
        window.addEventListener('keydown', handleKeyDown);
        return function () {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        isFocused,
        tilePosition,
        positionLock,
        disabled,
        id,
        selectedTool,
        setContextMenu,
        setSelectedTool,
        setManipulative,
    ]);
    // Cleanup timer on unmount
    useEffect(function () {
        return function () {
            if (keyMoveSaveTimer.current) {
                clearTimeout(keyMoveSaveTimer.current);
                keyMoveSaveTimer.current = null;
            }
        };
    }, []);
    // Reset isEditing when the tile loses focus (e.g. another tool is selected)
    useEffect(function () {
        if (!isFocused)
            setIsEditing(false);
    }, [isFocused]);
    // Click-outside handler: close editing when user clicks away (excluding math keyboard portals)
    useEffect(function () {
        if (!isEditing)
            return;
        var handleClickOutside = function (e) {
            var _a, _b;
            var target = e.target;
            if (((_a = labelWrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)) ||
                ((_b = labelWrapperCircleRef.current) === null || _b === void 0 ? void 0 : _b.contains(target)) ||
                target.closest('.ML__keyboard') ||
                target.closest('[part="keyboard"]') ||
                target.closest('.keypadWrapper') ||
                target.closest('.fraction-tile-math-btn')) {
                return;
            }
            setIsEditing(false);
        };
        window.addEventListener('mousedown', handleClickOutside, true);
        window.addEventListener('touchstart', handleClickOutside, true);
        return function () {
            window.removeEventListener('mousedown', handleClickOutside, true);
            window.removeEventListener('touchstart', handleClickOutside, true);
        };
    }, [isEditing]);
    var initialSelectedPositions = useRef({});
    var initialSelectedSnappedIds = useRef({});
    /** ---------------- DRAG START ---------------- */
    var onMouseDown = function (e) {
        var _a, _b, _c, _d, _e, _f, _g;
        // Skip if the event originated from a portal (e.g. math keypad portaled to body).
        // React bubbles portal events through the component tree even though the DOM target
        // is outside this element's subtree — so we guard using the DOM contains check.
        if (!e.currentTarget.contains(e.target))
            return;
        // Skip drag/selection when clicking inside a math-field during editing —
        // MathLive handles its own cursor placement and focus management.
        // Without this, the else-if below normalizes selectedTool to the bare tile ID,
        // which makes MathEditor's isFocused false → viewonly true, killing the math-field.
        var target = e.target;
        if (isEditing && (target.tagName === 'MATH-FIELD' || ((_a = target.closest) === null || _a === void 0 ? void 0 : _a.call(target, 'math-field'))))
            return;
        if ('touches' in e) {
            var target_1 = e.target;
            // Keypad touches must not update lastTouchStartTimeRef — otherwise a keypad
            // tap within 600ms of selecting the tile would satisfy the double-tap check
            // in onTouchEnd and open the context menu.
            var isKeypadTouch = ((_b = target_1.closest) === null || _b === void 0 ? void 0 : _b.call(target_1, '.keypadWrapper')) ||
                ((_c = target_1.closest) === null || _c === void 0 ? void 0 : _c.call(target_1, '.ML__keyboard')) ||
                ((_d = target_1.closest) === null || _d === void 0 ? void 0 : _d.call(target_1, '[part="keyboard"]')) ||
                ((_e = target_1.closest) === null || _e === void 0 ? void 0 : _e.call(target_1, '.fraction-tile-math-btn'));
            if (!isKeypadTouch) {
                lastTouchStartTimeRef.current = Date.now();
            }
            if (target_1.tagName !== 'INPUT' &&
                target_1.tagName !== 'TEXTAREA' &&
                target_1.tagName !== 'MATH-FIELD' &&
                !isKeypadTouch &&
                // VoiceOver double-tap: do NOT call preventDefault so the synthesised
                // click event is preserved — it is caught by the tile's onClick handler
                // which opens the context menu.  The spinner uses the same pattern.
                !lastFocusWasScreenReader.current) {
                e.preventDefault();
            }
        }
        if (disabled)
            return;
        // Any pointer event means this is not a VoiceOver swipe.
        // Clear the VO flag so the subsequent onFocus sets isEditing correctly.
        voiceFocusRef.current = false;
        pointerDownRef.current = true;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        // Math-keypad button taps must not set up the drag machinery — stopDrag would
        // fire on touchend and trigger setDragStart(null), causing a re-render that
        // interferes with the math-field focus timing and closes the MathLive keyboard.
        var isMathBtnTarget = (_g = (_f = target).closest) === null || _g === void 0 ? void 0 : _g.call(_f, '.fraction-tile-math-btn');
        // When this tile is grouped, selection/drag operates on the group's id
        // rather than the tile's own id — mirrors NumberAndMoneyPieceManipulative,
        // whose own mousedown handler drives group dragging directly rather than
        // delegating entirely to the sibling SelectionGroup overlay.
        var dragKey = groupId !== null && groupId !== void 0 ? groupId : id;
        if (!panningEnable && !isMathBtnTarget) {
            // --- MULTI-SELECT INITIALIZATION ---
            // Expand any manipulativeGroup_ token (this tile's own group, or another
            // group/tile already part of a broader multi-select) into its member
            // fraction-tile ids, so dragging moves every affected tile together.
            var rawIds = selectedTool.includes(dragKey) ? selectedTool : [dragKey];
            var relevantIds_1 = rawIds.flatMap(function (t) {
                return t.startsWith('manipulativeGroup_')
                    ? manipulative
                        .filter(function (m) { return m.data.type === 'fractionTiles' && m.data.groupId === t; })
                        .map(function (m) { return m.data.id; })
                    : [t];
            });
            var relevantTiles = manipulative.filter(function (m) { return m.data.id && relevantIds_1.includes(m.data.id) && m.data.type === 'fractionTiles'; });
            initialSelectedPositions.current = {};
            initialSelectedSnappedIds.current = {};
            relevantTiles.forEach(function (t) {
                if (t.data.id) {
                    initialSelectedPositions.current[t.data.id] = __assign({}, t.data.center);
                    initialSelectedSnappedIds.current[t.data.id] = t.data.snappedId;
                }
            });
            // -----------------------------------
            // Snapshot every selected tile's NL attachment as it stood at the
            // moment of mousedown, before anything else touches it — used both by
            // the real-drag restore below (once we know this is a drag, not a
            // click) and by stopDrag's undo-diff, for the LEADER and every
            // FOLLOWER alike (issue #409: a follower's drag-start detach needs its
            // own pre-drag snapshot too, or undo can't tell it ever changed).
            // Captured here rather than mutated here: a plain click (no movement)
            // must leave every tile completely untouched, or its position/
            // dimensions end up in a mismatched state (dimensions restored to
            // preSnapScale, but the top-left corner still where it was
            // flush-anchored for the OLD, number-line-driven scale) — which then
            // falls outside the number-line's snap threshold and can never
            // re-attach on a later click. See the `hasMoved` transition in
            // handleMove for where the actual restore happens once a real drag is
            // confirmed.
            var preDragNlSnapshot_1 = new Map();
            relevantTiles.forEach(function (t) {
                if (!t.data.id)
                    return;
                var d = t.data;
                preDragNlSnapshot_1.set(t.data.id, {
                    scale: d.scale,
                    numberLineId: d.numberLineId,
                    preSnapScale: d.preSnapScale,
                });
            });
            preMouseDownNlStateRef.current = preDragNlSnapshot_1;
            dragStartRef.current = {
                x: clientX,
                y: clientY,
            };
            hasMoved.current = false;
            setDragStart({
                x: clientX - tilePosition.x * zoomLevel,
                y: clientY - tilePosition.y * zoomLevel,
            });
            dragStated.current = true;
            // snappedId is cleared only once the drag threshold is crossed (in handleMove),
            // not here on mousedown — so a click without movement never loses the cluster.
            if (!selectedTool.some(function (t) { return t.startsWith(dragKey); })) {
                setSelectedTool(selectTool(e, dragKey, selectedTool, false));
                setSelectedManipulativeId(dragKey);
            }
            else if (!selectedTool.includes(dragKey)) {
                // Tile is selected via a suffixed ID (e.g. from label focus).
                // Normalize back to the bare tile ID so that downstream code using
                // exact includes() matches (scissors button, multi-select init, etc.).
                setSelectedTool([dragKey]);
                setSelectedManipulativeId(dragKey);
            }
            startMultiDrag();
        }
    };
    /** ---------------- HANDLE DRAG, ROLL ON CLICK ---------------- */
    useEffect(function () {
        var handleMove = function (e) {
            var _a, _b, _c, _d;
            if ('touches' in e) {
                e.preventDefault();
            }
            var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
            var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
            if (!dragStartRef.current)
                return;
            // movement threshold
            var dx = clientX - dragStartRef.current.x;
            var dy = clientY - dragStartRef.current.y;
            if (!hasMoved.current && Math.sqrt(dx * dx + dy * dy) > 5) {
                hasMoved.current = true;
                // Select this tile (or its group, if grouped) when drag starts. Using
                // the bare `id` here would re-select just this tile the moment real
                // dragging begins, undoing the group selection made at mousedown.
                var dragKey = groupId !== null && groupId !== void 0 ? groupId : id;
                setSelectedTool(selectTool(e, dragKey, selectedToolRef.current, false));
                setSelectedManipulativeId(dragKey);
                // Clear snappedId exactly once — only when we're sure this is a real drag, not a click.
                // initialSelectedSnappedIds was captured at mousedown so undo still works correctly.
                var draggedIds_1 = new Set(Object.keys(initialSelectedSnappedIds.current));
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        if (item.data.type !== 'fractionTiles')
                            return item;
                        var d = item.data;
                        if (!d.snappedId || !draggedIds_1.has(d.id))
                            return item;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, d), { snappedId: undefined }) });
                    });
                });
                // If this tile currently has `numberLineId` set, its live scale is
                // still NL-driven. Restore scale/dimensions to `preSnapScale` now
                // that we know this is a real drag — not at mousedown, or a plain
                // click (no movement) would shrink the tile in place without ever
                // repositioning it, breaking its flush alignment against the number
                // line for no reason (see the comment on `preMouseDownNlStateRef`
                // above, in onMouseDown). Every downstream calculation during the
                // rest of THIS drag (preview ghost, tile-to-tile snap, re-check for
                // a number line) then naturally sees the correct restored size. If
                // the tile ends up dropped back near a number line, the existing
                // NL-check in stopDrag re-applies the NL's scale/numberLineId anyway.
                // Detach every selected fractionTile that's number-line-attached, not
                // just the leader (`id`) — a FOLLOWER dragged alongside an ordinary
                // (non-NL) leader used to keep its number-line-forced scale for the
                // whole drag, since this restore previously only ran for `id` itself
                // (issue #409). Restoring at drag-start (not mousedown) matters for
                // the same reason noted above — a plain click must not shrink a tile
                // in place without ever repositioning it.
                var selectedIds = new Set(Object.keys(initialSelectedPositions.current));
                selectedIds.add(id);
                var nlRestoreUpdates_1 = new Map();
                selectedIds.forEach(function (selId) {
                    var _a, _b, _c, _d;
                    var selData = (_a = manipulativeRef.current.find(function (m) { return m.data.id === selId; })) === null || _a === void 0 ? void 0 : _a.data;
                    if (!selData || selData.type !== 'fractionTiles' || !selData.numberLineId)
                        return;
                    // `preSnapScale` holds the shared/global scale (kept fresh on every
                    // snap — see stopDrag's write site) so it's the primary source;
                    // fall back to the CURRENT scale of another non-NL-attached sibling
                    // of the same shape type only if this tile somehow never had one
                    // recorded.
                    var siblingScale = (_c = (_b = manipulativeRef.current.find(function (m) {
                        return m.data.type === 'fractionTiles' &&
                            m.data.id !== selId &&
                            m.data.shapeType === selData.shapeType &&
                            !m.data.numberLineId;
                    })) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.scale;
                    var restoredScale = (_d = selData.preSnapScale) !== null && _d !== void 0 ? _d : siblingScale;
                    if (restoredScale === undefined)
                        return;
                    var baseWidth = selData.dimensions.width / (selData.scale || 1);
                    var baseHeight = selData.dimensions.height / (selData.scale || 1);
                    nlRestoreUpdates_1.set(selId, {
                        scale: restoredScale,
                        width: baseWidth * restoredScale,
                        height: baseHeight * restoredScale,
                    });
                });
                if (nlRestoreUpdates_1.size > 0) {
                    setManipulative(function (prev) {
                        return prev.map(function (item) {
                            var update = item.data.id ? nlRestoreUpdates_1.get(item.data.id) : undefined;
                            if (!update)
                                return item;
                            return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: update.scale, dimensions: { width: update.width, height: update.height }, numberLineId: undefined, preSnapScale: undefined }) });
                        });
                    });
                }
            }
            if (!hasMoved.current)
                return;
            if (positionLock)
                return;
            // Calculate absolute position of the LEADER (dragged item)
            var newX = (clientX - ((_a = dragStart === null || dragStart === void 0 ? void 0 : dragStart.x) !== null && _a !== void 0 ? _a : 0)) / zoomLevelRef.current;
            var newY = (clientY - ((_b = dragStart === null || dragStart === void 0 ? void 0 : dragStart.y) !== null && _b !== void 0 ? _b : 0)) / zoomLevelRef.current;
            // Calculate DELTA from initial position of the leader
            var initialLeaderPos = initialSelectedPositions.current[id];
            // Fallback if not found (shouldn't happen directly)
            var startX = initialLeaderPos ? initialLeaderPos.x : tilePosition.x;
            var startY = initialLeaderPos ? initialLeaderPos.y : tilePosition.y;
            var deltaX = newX - startX;
            var deltaY = newY - startY;
            setTilePosition({ x: newX, y: newY });
            // Multi-select: broadcast delta so OTHER-type items follow
            moveMultiDrag(deltaX, deltaY);
            // Update followers in the same synchronous pass as the leader's own
            // setTilePosition above (matching Counter.tsx's multi-select-move
            // pattern — see docs/features/MultiSelectMove.md) so React batches
            // leader + followers into one render/paint. Throttling this to a
            // requestAnimationFrame callback (the previous approach) decoupled
            // followers from the leader's per-mousemove-event update, causing
            // followers to visibly lag the leader by a frame or two.
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    // If this item is in the initially selected group, move it by delta
                    if (item.data.id &&
                        initialSelectedPositions.current[item.data.id] &&
                        item.data.type === 'fractionTiles') {
                        var startStats = initialSelectedPositions.current[item.data.id];
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: {
                                    x: startStats.x + deltaX,
                                    y: startStats.y + deltaY,
                                } }) });
                    }
                    return item;
                });
            });
            // --- SNAPPING PREVIEW ---
            // Determine if the current drag location allows for snapping
            // Pass ALL selected IDs to ignore them in snapping checks
            var relevantIds = Object.keys(initialSelectedPositions.current);
            // Construct draggedGroup for multi-select snapping
            var draggedGroup = [];
            var leaderInitial = initialSelectedPositions.current[id];
            if (leaderInitial) {
                Object.keys(initialSelectedPositions.current).forEach(function (key) {
                    var item = manipulativeRef.current.find(function (m) { return m.data.id === key; });
                    if (item && item.data.type === 'fractionTiles') {
                        var d = item.data;
                        var initialPos = initialSelectedPositions.current[key];
                        draggedGroup.push({
                            id: key,
                            offset: { x: initialPos.x - leaderInitial.x, y: initialPos.y - leaderInitial.y },
                            dimensions: d.dimensions,
                        });
                    }
                });
            }
            // If this tile currently has `numberLineId` set, use its
            // preSnapScale-derived size for the tile-to-tile ghost check instead of
            // the still-NL-scaled `tileSizeRef.current`, so the preview matches
            // what will actually happen on drop. Otherwise, prefer matching an
            // adjacent existing tile's own rendered scale directly — that
            // neighbor's size is ground truth for what a docked tile must match.
            // Only fall back to a number-line proximity guess when there's no
            // neighbor to match. Without this the ghost renders at the dragged
            // tile's own (still larger/inherited) size while the real drop lands at
            // the corrected size, so the ghost visually overstates where the tile
            // will actually end up.
            var draggedDataForPreview = (_c = manipulativeRef.current.find(function (m) { return m.data.id === id; })) === null || _c === void 0 ? void 0 : _c.data;
            // Baseline size for the dock solve: the size the tile would have if it
            // docked against nothing. `solveDockMatchingScale` below corrects it to
            // the dock target's own scale and re-solves, so this only has to be
            // right for the no-dock case.
            var baselinePreviewDims;
            if ((draggedDataForPreview === null || draggedDataForPreview === void 0 ? void 0 : draggedDataForPreview.numberLineId) && draggedDataForPreview.preSnapScale !== undefined) {
                baselinePreviewDims = {
                    width: defaultDimension.width * draggedDataForPreview.preSnapScale,
                    height: defaultDimension.height * draggedDataForPreview.preSnapScale,
                };
            }
            else {
                var prospectiveNlSnapForPreview = shapeType === 'rectangle'
                    ? snapTileToNumberLine({ x: newX, y: newY }, defaultDimension, scale, manipulativeRef.current, id)
                    : null;
                baselinePreviewDims = prospectiveNlSnapForPreview
                    ? {
                        width: defaultDimension.width * prospectiveNlSnapForPreview.scale,
                        height: defaultDimension.height * prospectiveNlSnapForPreview.scale,
                    }
                    : tileSizeRef.current;
            }
            // Two-pass solve: size the ghost to the tile it will dock against
            // BEFORE the dock x/y is computed, then render the ghost at exactly
            // that size and position. See solveDockMatchingScale.
            var previewTileToTileDims = baselinePreviewDims;
            var previewDockedTo;
            var previewSnap;
            if (shapeType === 'rectangle') {
                var solved = solveDockMatchingScale({ x: newX, y: newY }, baselinePreviewDims, defaultDimension, manipulativeRef.current, relevantIds, draggedGroup);
                previewSnap = solved.snap;
                previewTileToTileDims = solved.dimensions;
                previewDockedTo = solved.dockedTo;
            }
            else {
                previewSnap = getSnappedPosition({ x: newX, y: newY }, previewTileToTileDims, shapeType, manipulativeRef.current, relevantIds, draggedGroup);
            }
            var snappedX = previewSnap.x, snappedY = previewSnap.y, snappedRotation = previewSnap.rotation, isSnappedX = previewSnap.isSnappedX, isSnappedY = previewSnap.isSnappedY, previewModifications = previewSnap.modifications;
            // --- NUMBER LINE SNAPPING LOGIC ---
            var isNLSnapped = false;
            var nlPreviewX = snappedX;
            var nlPreviewY = snappedY;
            var nlSnapScale = null;
            var isDockedForPreview = isSnappedX || isSnappedY;
            if (shapeType === 'rectangle') {
                // The number-line candidate is ALWAYS computed, even when the tile is
                // already docked against a neighbor. Short-circuiting on an adjacent
                // neighbor (as this used to) meant tile-to-tile silently won every
                // time a neighbor existed, so a tile dragged over the number line next
                // to an existing tile previewed at the dock position while the drop
                // applied the number line's scale — the ghost landed centered on its
                // neighbor instead of on the nearer tick.
                var nlSnap = snapTileToNumberLine({ x: newX, y: newY }, defaultDimension, scale, manipulativeRef.current, id);
                if (nlSnap && isDockedForPreview) {
                    // On the number line AND docked to a neighbor: both are real
                    // candidates, so whichever lands nearer the raw drag point wins.
                    var dockDist = Math.hypot(snappedX - newX, snappedY - newY);
                    var nlDist = Math.hypot(nlSnap.x - newX, nlSnap.y - newY);
                    if (nlDist < dockDist) {
                        nlPreviewX = nlSnap.x;
                        nlPreviewY = nlSnap.y;
                        nlSnapScale = nlSnap.scale;
                        isNLSnapped = true;
                    }
                    else {
                        // Dock won on POSITION — keep tile-to-tile adjacency authoritative
                        // there. Scale is a separate question: any tile landing on a
                        // number line takes that line's scale, so fall back to the line's
                        // own scale when the neighbor isn't recognised. `adjacentForPreview`
                        // is computed from the dragged tile's PRE-SNAP box, so on a tile's
                        // first attach beside an already-attached (larger) neighbor the
                        // adjacency test misses and this used to leave `nlSnapScale` null —
                        // drawing the ghost at the pre-snap size while the drop landed at
                        // the number line's size.
                        nlSnapScale = (_d = previewDockedTo === null || previewDockedTo === void 0 ? void 0 : previewDockedTo.scale) !== null && _d !== void 0 ? _d : nlSnap.scale;
                    }
                }
                else if (nlSnap) {
                    nlPreviewX = nlSnap.x;
                    nlPreviewY = nlSnap.y;
                    nlSnapScale = nlSnap.scale;
                    isNLSnapped = true;
                }
                else if (isDockedForPreview && previewDockedTo) {
                    // Not on the number line at all — tile-to-tile always wins.
                    nlSnapScale = previewDockedTo.scale;
                }
            }
            // --- END NUMBER LINE SNAPPING LOGIC ---
            // --- GRID SNAPPING LOGIC ---
            var finalPreviewX = isNLSnapped ? nlPreviewX : snappedX;
            var finalPreviewY = isNLSnapped ? nlPreviewY : snappedY;
            var GRID_SIZE = 55;
            if (playerBgPattern === 'square' &&
                shapeType === 'rectangle' &&
                !isSnappedX &&
                !isSnappedY &&
                !isNLSnapped) {
                finalPreviewX = Math.round(newX / GRID_SIZE) * GRID_SIZE;
                finalPreviewY = Math.round(newY / GRID_SIZE) * GRID_SIZE;
            }
            // --- END GRID SNAPPING LOGIC ---
            // Visibility Logic:
            // - Rectangles: Show ghost indicator only when snapping to a shape or grid.
            // - Circles: Only show the ghost when it's actually snapping to another circle center.
            var isShapeSnapped = isSnappedX || isSnappedY;
            var isGridSnapped = playerBgPattern === 'square' &&
                shapeType === 'rectangle' &&
                !isShapeSnapped &&
                !isNLSnapped;
            if (isShapeSnapped || isNLSnapped || isGridSnapped) {
                setSnapPreview({
                    x: finalPreviewX,
                    y: finalPreviewY,
                    rotation: snappedRotation !== null && snappedRotation !== void 0 ? snappedRotation : rotation,
                    group: draggedGroup.map(function (g) { return ({
                        offset: g.offset,
                        dimensions: nlSnapScale
                            ? {
                                width: defaultDimension.width * nlSnapScale,
                                height: defaultDimension.height * nlSnapScale,
                            }
                            : g.dimensions,
                        id: g.id,
                    }); }),
                    modifications: previewModifications,
                });
            }
            else {
                setSnapPreview(null);
            }
        };
        var stopDrag = function (e) {
            var _a, _b, _c, _d;
            if (!dragStartRef.current)
                return;
            setSnapPreview(null);
            if (hasMoved.current && !positionLock) {
                // --- SNAPPING LOGIC (ON DROP) ---
                var rawFinal = tilePosRef.current;
                var relevantIds = Object.keys(initialSelectedPositions.current);
                // Construct draggedGroup for multi-select snapping
                var draggedGroup_1 = [];
                var leaderInitial_1 = initialSelectedPositions.current[id];
                if (leaderInitial_1) {
                    Object.keys(initialSelectedPositions.current).forEach(function (key) {
                        var item = manipulativeRef.current.find(function (m) { return m.data.id === key; });
                        if (item && item.data.type === 'fractionTiles') {
                            var d = item.data;
                            var initialPos = initialSelectedPositions.current[key];
                            draggedGroup_1.push({
                                id: key,
                                offset: { x: initialPos.x - leaderInitial_1.x, y: initialPos.y - leaderInitial_1.y },
                                dimensions: d.dimensions,
                            });
                        }
                    });
                }
                var draggedData = (_a = manipulativeRef.current.find(function (m) { return m.data.id === id; })) === null || _a === void 0 ? void 0 : _a.data;
                // If THIS tile currently has `numberLineId` set, it's the one being
                // unsnapped/dragged away — `tileSizeRef.current` still reflects the
                // NL-driven size, but dropping it away from any number line restores
                // `scale`/`dimensions` to `preSnapScale` AFTER this snap position is
                // computed (see the NL-unsnap branch below). Using the still-NL-scaled
                // size here computes a dock position for a size the tile is about to
                // stop being, producing a gap/overlap once the restore lands — so use
                // the pre-snap size as the tentative size for this check instead.
                // Checked BEFORE matching an adjacent tile below: this tile's own
                // pending unsnap takes priority over incidentally touching some other
                // (possibly still NL-attached) neighbor tile mid-drag, or dragging it
                // away from the line would incorrectly re-adopt that neighbor's scale.
                var baselineDropDims = void 0;
                if ((draggedData === null || draggedData === void 0 ? void 0 : draggedData.numberLineId) && draggedData.preSnapScale !== undefined) {
                    baselineDropDims = {
                        width: defaultDimension.width * draggedData.preSnapScale,
                        height: defaultDimension.height * draggedData.preSnapScale,
                    };
                }
                else {
                    // Prospective NL check: if this (not-yet-NL-attached) tile is about
                    // to dock next to an already NL-attached neighbor, the dock geometry
                    // below must use the number line's prospective scale, not this
                    // tile's own current scale — otherwise a gap/overlap opens once the
                    // NL-scale correction lands (below) after docking already computed
                    // an edge position using the wrong width/height.
                    var prospectiveNlSnap = shapeType === 'rectangle'
                        ? snapTileToNumberLine(rawFinal, defaultDimension, scale, manipulativeRef.current, id)
                        : null;
                    baselineDropDims = prospectiveNlSnap
                        ? {
                            width: defaultDimension.width * prospectiveNlSnap.scale,
                            height: defaultDimension.height * prospectiveNlSnap.scale,
                        }
                        : tileSizeRef.current;
                }
                // Two-pass solve — the SAME call the drag preview makes, so the ghost
                // and the drop cannot disagree about either the size or the position.
                // `adjacentAtDrop` is the tile actually docked to, taken from the
                // solver's own choice rather than a separate near-exact contact test
                // that missed most of the 55px catch radius.
                var tileToTileDims = baselineDropDims;
                var adjacentAtDrop_1;
                var dropSnap = void 0;
                if (shapeType === 'rectangle') {
                    var solved = solveDockMatchingScale(rawFinal, baselineDropDims, defaultDimension, manipulativeRef.current, relevantIds, draggedGroup_1);
                    dropSnap = solved.snap;
                    tileToTileDims = solved.dimensions;
                    adjacentAtDrop_1 = solved.dockedTo;
                }
                else {
                    dropSnap = getSnappedPosition(rawFinal, tileToTileDims, shapeType, manipulativeRef.current, relevantIds, draggedGroup_1);
                }
                var snappedX = dropSnap.x, snappedY = dropSnap.y, snappedRotation_1 = dropSnap.rotation, modifications_1 = dropSnap.modifications, isSnappedX = dropSnap.isSnappedX, isSnappedY = dropSnap.isSnappedY;
                // Round X to nearest 0.5px (preserves sub-pixel alignment for tiles with 0.5-unit widths
                // like 1/8 at odd integer scales). Round Y to nearest integer to fix row misalignment.
                var final_1 = { x: Math.round(snappedX * 2) / 2, y: Math.round(snappedY) };
                // --- NUMBER LINE SNAP ON DROP ---
                // When docked to a neighbor whose own scale we already matched
                // (adjacentAtDrop), that dock position is already geometrically
                // correct and needs no further correction — just adopt the same
                // scale/numberLineId (if the neighbor has one) as the drop result.
                // Otherwise fall back to the number-line proximity check, which is
                // authoritative for position when there's no neighbor to match: its y
                // is derived directly from real tick geometry, while docking's y can
                // be off if it used a mismatched height.
                var isTileDocked = isSnappedX || isSnappedY;
                var nlSnapResult_1 = null;
                if (shapeType === 'rectangle') {
                    // Mirrors the drag-preview precedence exactly (see the NUMBER LINE
                    // SNAPPING LOGIC block in the mousemove handler): the number-line
                    // candidate is ALWAYS computed, even when docked to a neighbor, so
                    // that "nearest wins" can actually be evaluated. Short-circuiting on
                    // an adjacent neighbor made tile-to-tile win unconditionally, and
                    // the preview and the drop then disagreed about where the tile goes.
                    var prospectiveNl = snapTileToNumberLine(rawFinal, defaultDimension, scale, manipulativeRef.current, id);
                    var adoptNeighbor = function () {
                        // Dock won — tile-to-tile adjacency stays authoritative for
                        // position; just inherit the neighbor's NL attachment (if any).
                        if (adjacentAtDrop_1 === null || adjacentAtDrop_1 === void 0 ? void 0 : adjacentAtDrop_1.numberLineId) {
                            nlSnapResult_1 = {
                                x: final_1.x,
                                y: final_1.y,
                                scale: adjacentAtDrop_1.scale,
                                numberLineId: adjacentAtDrop_1.numberLineId,
                            };
                        }
                    };
                    if (prospectiveNl && isTileDocked) {
                        // On the number line AND docked: nearest to the raw drop wins.
                        var dockDist = Math.hypot(final_1.x - rawFinal.x, final_1.y - rawFinal.y);
                        var nlDist = Math.hypot(prospectiveNl.x - rawFinal.x, prospectiveNl.y - rawFinal.y);
                        if (nlDist < dockDist) {
                            final_1.x = prospectiveNl.x;
                            final_1.y = prospectiveNl.y;
                            nlSnapResult_1 = prospectiveNl;
                        }
                        else {
                            adoptNeighbor();
                        }
                    }
                    else if (prospectiveNl) {
                        final_1.x = prospectiveNl.x;
                        final_1.y = prospectiveNl.y;
                        nlSnapResult_1 = prospectiveNl;
                    }
                    else if (isTileDocked) {
                        // Not on the number line at all — tile-to-tile always wins.
                        adoptNeighbor();
                    }
                }
                // --- END NUMBER LINE SNAP ON DROP ---
                // --- GRID SNAP ON DROP ---
                var GRID_SIZE = 55;
                if (playerBgPattern === 'square' &&
                    shapeType === 'rectangle' &&
                    !isSnappedX &&
                    !isSnappedY &&
                    !nlSnapResult_1) {
                    final_1.x = Math.round(rawFinal.x / GRID_SIZE) * GRID_SIZE;
                    final_1.y = Math.round(rawFinal.y / GRID_SIZE) * GRID_SIZE;
                }
                // --- END GRID SNAP ON DROP ---
                // Ensure sub-pixel aligned canvas units for free drops too (no grid/tile snap applied)
                final_1 = { x: Math.round(final_1.x * 2) / 2, y: Math.round(final_1.y) };
                // Update local state to snapped position
                setTilePosition(final_1);
                // Play snap sound when the tile snaps to another tile or number line
                if (isSnappedX || isSnappedY || nlSnapResult_1) {
                    playAudio('PatternAdd');
                }
                // --- END SNAPPING LOGIC ---
                var currentManipulative_1 = manipulativeRef.current;
                // The scale to restore to when this tile unsnaps from a number line.
                // `preSnapScale` holds the shared/global fraction-tile scale — the
                // CURRENT scale of any other non-NL-attached tile of the same shape
                // type — rather than a one-time snapshot, so it never goes stale even
                // if the rest of the group was rescaled via scalePlus/scaleMinus while
                // this tile stayed NL-attached. It's (re)computed and written below on
                // every snap, and only falls back to this tile's own current scale
                // when no such sibling exists.
                var currentGroupScale_1 = (_c = (_b = currentManipulative_1.find(function (m) {
                    return m.data.type === 'fractionTiles' &&
                        m.data.id !== id &&
                        m.data.shapeType === shapeType &&
                        !m.data.numberLineId;
                })) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.scale;
                // Calculate final Delta based on Leader's snap
                var initialLeaderPos = initialSelectedPositions.current[id];
                var startX = initialLeaderPos ? initialLeaderPos.x : rawFinal.x;
                var startY = initialLeaderPos ? initialLeaderPos.y : rawFinal.y;
                var finalDeltaX_1 = final_1.x - startX;
                var finalDeltaY_1 = final_1.y - startY;
                // 1. Calculate the new state for the dragged tile itself AND apply modifications to others
                var _newState_2 = currentManipulative_1.map(function (item) {
                    var _a;
                    // Check if this item needs to be modified (shifted) - usually for unselected items
                    var mod = modifications_1 === null || modifications_1 === void 0 ? void 0 : modifications_1.find(function (m) { return m.id === item.data.id; });
                    if (item.data.id && initialSelectedPositions.current[item.data.id]) {
                        // It's part of the selected group!
                        var startStats = initialSelectedPositions.current[item.data.id];
                        var newCenter = item.data.id === id
                            ? final_1
                            : { x: startStats.x + finalDeltaX_1, y: startStats.y + finalDeltaY_1 };
                        var newRotation = item.data.id === id && snappedRotation_1 !== undefined
                            ? snappedRotation_1
                            : item.data.rotation;
                        var updatedData = __assign(__assign({}, item.data), { center: newCenter, rotation: newRotation });
                        // Apply NL snap scale + reference for the leader tile
                        if (item.data.id === id && nlSnapResult_1) {
                            // Recompute on every snap (first snap or re-drop onto the same/a
                            // different line) — never keep an old stored value here, or a
                            // rescale of the group while this tile stayed NL-attached would
                            // leave preSnapScale pointing at a stale, no-longer-shared scale.
                            updatedData.preSnapScale = currentGroupScale_1 !== null && currentGroupScale_1 !== void 0 ? currentGroupScale_1 : scale;
                            updatedData.scale = nlSnapResult_1.scale;
                            updatedData.numberLineId = nlSnapResult_1.numberLineId;
                            // Keep dimensions consistent with the new scale in the SAME write —
                            // leaving dimensions stale here (at the pre-snap scale) creates a
                            // one-render window where scale/dimensions disagree, which the
                            // dimension-recalculation effect below then "corrects" by writing
                            // again, re-triggering this component's own scale-sync effect.
                            updatedData.dimensions = {
                                width: defaultDimension.width * nlSnapResult_1.scale,
                                height: defaultDimension.height * nlSnapResult_1.scale,
                            };
                        }
                        else if (item.data.id === id && !nlSnapResult_1) {
                            var prevData = item.data;
                            // Restore the scale the tile had before it snapped onto a number
                            // line — otherwise it silently keeps the number line's scale after
                            // being dragged away. `preSnapScale` is the shared/global scale
                            // and is kept fresh on every snap, so it's the primary source;
                            // currentGroupScale (recomputed live, right now) is only needed
                            // as a fallback if this tile somehow never had one recorded.
                            if (prevData.numberLineId) {
                                var restoreScale = (_a = prevData.preSnapScale) !== null && _a !== void 0 ? _a : currentGroupScale_1;
                                if (restoreScale !== undefined) {
                                    updatedData.scale = restoreScale;
                                    updatedData.dimensions = {
                                        width: defaultDimension.width * restoreScale,
                                        height: defaultDimension.height * restoreScale,
                                    };
                                }
                            }
                            updatedData.numberLineId = undefined;
                            updatedData.preSnapScale = undefined;
                        }
                        return __assign(__assign({}, item), { data: updatedData });
                    }
                    else if (mod) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: mod.center }) });
                    }
                    else {
                        return item;
                    }
                });
                // Batch all undo entries for this drag as a single history step
                var dragStateChanges_1 = [];
                if (modifications_1) {
                    modifications_1.forEach(function (mod) {
                        var originalItem = currentManipulative_1.find(function (m) { return m.data.id === mod.id; });
                        if (originalItem) {
                            dragStateChanges_1.push({
                                type: mod.id,
                                id: mod.id.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(originalItem.data.center),
                                new: JSON.stringify(mod.center),
                            });
                        }
                    });
                }
                // Save state for SELECTED GROUP
                Object.keys(initialSelectedPositions.current).forEach(function (itemId) {
                    var _a, _b;
                    var oldPos = initialSelectedPositions.current[itemId];
                    var newItem = _newState_2.find(function (m) { return m.data.id === itemId; });
                    if (newItem) {
                        var newPos = newItem.data.center;
                        if (oldPos.x !== newPos.x || oldPos.y !== newPos.y) {
                            dragStateChanges_1.push({
                                type: itemId,
                                id: itemId.split('_')[1],
                                propertyName: 'center',
                                old: JSON.stringify(oldPos),
                                new: JSON.stringify(newPos),
                            });
                        }
                        // Handle Rotation for leader
                        if (itemId === id && snappedRotation_1 !== undefined) {
                            dragStateChanges_1.push({
                                type: id,
                                id: id.split('_')[1],
                                propertyName: 'rotation',
                                old: JSON.stringify((_a = props.rotation) !== null && _a !== void 0 ? _a : 0),
                                new: JSON.stringify(snappedRotation_1),
                            });
                        }
                        // Handle NL snap/unsnap scale change for leader — covers both
                        // directions: snapping onto a number line (scale forced to the
                        // line's scale) and unsnapping away from one (scale restored from
                        // preSnapScale). Without recording both, undo would revert
                        // position but leave scale/dimensions/numberLineId/preSnapScale
                        // in their post-drop state.
                        // A FOLLOWER never gets an `nlSnapResult` of its own (that's only
                        // solved for the leader's drop position), but it can still have
                        // been detached by the drag-start restore above (issue #409) —
                        // so it needs the same before/after diff the leader gets, just
                        // without the nlSnapResult branch. Use the value captured at
                        // mousedown, before the eager preSnapScale restore ran —
                        // manipulativeRef.current by this point already reflects that
                        // restore, which would make an NL-attached-at-drag-start tile
                        // look unchanged here and silently drop its undo entry.
                        var preDragSnapshot = preMouseDownNlStateRef.current.get(itemId);
                        if (preDragSnapshot) {
                            var preDragScale = preDragSnapshot.scale;
                            var preDragNumberLineId = preDragSnapshot.numberLineId;
                            var preDragPreSnapScale = preDragSnapshot.preSnapScale;
                            var itemNewData = (_b = newItem.data) !== null && _b !== void 0 ? _b : undefined;
                            if (itemId === id && nlSnapResult_1 && nlSnapResult_1.scale !== preDragScale) {
                                dragStateChanges_1.push({
                                    type: itemId,
                                    id: itemId.split('_')[1],
                                    propertyName: 'scale',
                                    old: JSON.stringify(preDragScale),
                                    new: JSON.stringify(nlSnapResult_1.scale),
                                });
                            }
                            else if (!(itemId === id && nlSnapResult_1) &&
                                itemNewData &&
                                itemNewData.scale !== preDragScale) {
                                dragStateChanges_1.push({
                                    type: itemId,
                                    id: itemId.split('_')[1],
                                    propertyName: 'scale',
                                    old: JSON.stringify(preDragScale),
                                    new: JSON.stringify(itemNewData.scale),
                                });
                            }
                            if (itemNewData && itemNewData.numberLineId !== preDragNumberLineId) {
                                dragStateChanges_1.push({
                                    type: itemId,
                                    id: itemId.split('_')[1],
                                    propertyName: 'numberLineId',
                                    old: JSON.stringify(preDragNumberLineId),
                                    new: JSON.stringify(itemNewData.numberLineId),
                                });
                            }
                            if (itemNewData && itemNewData.preSnapScale !== preDragPreSnapScale) {
                                dragStateChanges_1.push({
                                    type: itemId,
                                    id: itemId.split('_')[1],
                                    propertyName: 'preSnapScale',
                                    old: JSON.stringify(preDragPreSnapScale),
                                    new: JSON.stringify(itemNewData.preSnapScale),
                                });
                            }
                        }
                    }
                });
                // --- SNAPPED ID ASSIGNMENT ---
                // Must run before saveState so old snappedId values can be captured for undo.
                if (shapeType === 'rectangle') {
                    var droppedIds_1 = new Set(Object.keys(initialSelectedPositions.current));
                    var modIds_1 = new Set((modifications_1 !== null && modifications_1 !== void 0 ? modifications_1 : []).map(function (m) { return m.id; }));
                    // Adjacency check: horizontal (same row, touching left/right) or vertical (same column, touching top/bottom).
                    var isTileAdjacentToDropped_1 = function (d) {
                        if (d.shapeType !== 'rectangle')
                            return false;
                        return _newState_2.some(function (dropped) {
                            var _a, _b, _c, _d, _e, _f;
                            if (!dropped.data.id || !droppedIds_1.has(dropped.data.id))
                                return false;
                            var dd = dropped.data;
                            if (dd.shapeType !== 'rectangle')
                                return false;
                            var sameRow = Math.abs(dd.center.y - d.center.y) < 10;
                            var sameCol = dd.center.x < d.center.x + d.dimensions.width + 5 &&
                                dd.center.x + ((_b = (_a = dd.dimensions) === null || _a === void 0 ? void 0 : _a.width) !== null && _b !== void 0 ? _b : 0) > d.center.x - 5;
                            if (sameRow) {
                                var myRight = dd.center.x + ((_d = (_c = dd.dimensions) === null || _c === void 0 ? void 0 : _c.width) !== null && _d !== void 0 ? _d : 0);
                                return (Math.abs(d.center.x - myRight) < 5 ||
                                    Math.abs(d.center.x + d.dimensions.width - dd.center.x) < 5);
                            }
                            if (sameCol) {
                                var myBottom = dd.center.y + ((_f = (_e = dd.dimensions) === null || _e === void 0 ? void 0 : _e.height) !== null && _f !== void 0 ? _f : 0);
                                return (Math.abs(d.center.y - myBottom) < 5 ||
                                    Math.abs(d.center.y + d.dimensions.height - dd.center.y) < 5);
                            }
                            return false;
                        });
                    };
                    // Resolve the cluster ID: inherit from an adjacent tile, or create a new UUID.
                    var resolvedSnappedId = void 0;
                    for (var _i = 0, _newState_1 = _newState_2; _i < _newState_1.length; _i++) {
                        var item = _newState_1[_i];
                        if (item.data.type !== 'fractionTiles')
                            continue;
                        var d = item.data;
                        if (droppedIds_1.has(d.id) || modIds_1.has(d.id))
                            continue;
                        if (d.shapeType !== 'rectangle' || d.center == null)
                            continue;
                        if (isTileAdjacentToDropped_1(d) && d.snappedId) {
                            resolvedSnappedId = d.snappedId;
                            break;
                        }
                    }
                    if (!resolvedSnappedId)
                        resolvedSnappedId = uuidv4();
                    var finalSnappedId_1 = resolvedSnappedId;
                    // Record snappedId changes for undo/redo before mutating _newState.
                    // Use initialSelectedSnappedIds for dropped tiles because drag-start already
                    // cleared their snappedId from state — d.snappedId would be undefined at this point.
                    _newState_2.forEach(function (item) {
                        var _a, _b;
                        if (item.data.type !== 'fractionTiles')
                            return;
                        var d = item.data;
                        var isDropped = droppedIds_1.has(d.id) || modIds_1.has(d.id);
                        var isAdj = !isDropped && isTileAdjacentToDropped_1(d);
                        if (!isDropped && !isAdj)
                            return;
                        // Pre-drag snappedId: for dropped tiles use captured value; for adjacent tiles use current value.
                        var preDragSnappedId = isDropped
                            ? ((_a = initialSelectedSnappedIds.current[d.id]) !== null && _a !== void 0 ? _a : null)
                            : ((_b = d.snappedId) !== null && _b !== void 0 ? _b : null);
                        if (preDragSnappedId !== finalSnappedId_1) {
                            dragStateChanges_1.push({
                                type: d.id,
                                id: d.id.split('_')[1],
                                propertyName: 'snappedId',
                                old: JSON.stringify(preDragSnappedId),
                                new: JSON.stringify(finalSnappedId_1),
                            });
                        }
                    });
                    // Apply finalSnappedId to dropped tiles, modification tiles, and their adjacents.
                    _newState_2 = _newState_2.map(function (item) {
                        if (item.data.type !== 'fractionTiles')
                            return item;
                        var d = item.data;
                        if (droppedIds_1.has(d.id) || modIds_1.has(d.id)) {
                            return __assign(__assign({}, item), { data: __assign(__assign({}, d), { snappedId: finalSnappedId_1 }) });
                        }
                        if (d.snappedId && d.snappedId !== finalSnappedId_1)
                            return item;
                        if (d.center == null)
                            return item;
                        if (isTileAdjacentToDropped_1(d))
                            return __assign(__assign({}, item), { data: __assign(__assign({}, d), { snappedId: finalSnappedId_1 }) });
                        return item;
                    });
                }
                // --- END SNAPPED ID ASSIGNMENT ---
                // --- SNAPPED TO ASSIGNMENT (sole source of truth for scale repositioning) ---
                // Determine which single tile (if any) each dropped/shifted tile is now
                // edge-touching, and record that as `snappedTo`. Any tile that was
                // snapped but is no longer touching its parent post-drop has its
                // `snappedTo` explicitly cleared -- never left stale.
                if (shapeType === 'rectangle') {
                    var droppedIds = new Set(Object.keys(initialSelectedPositions.current));
                    var modIds = new Set((modifications_1 !== null && modifications_1 !== void 0 ? modifications_1 : []).map(function (m) { return m.id; }));
                    var movedIds_1 = new Set();
                    droppedIds.forEach(function (idKey) { return movedIds_1.add(idKey); });
                    modIds.forEach(function (idKey) { return movedIds_1.add(idKey); });
                    _newState_2 = _newState_2.map(function (item) {
                        var _a;
                        if (item.data.type !== 'fractionTiles')
                            return item;
                        var d = item.data;
                        if (!movedIds_1.has(d.id))
                            return item;
                        var candidates = _newState_2
                            .filter(function (m) { return m.data.type === 'fractionTiles' && m.data.id !== d.id; })
                            .map(function (m) { return m.data; });
                        var rel = (_a = findTouchingParent(d.center, d.dimensions, candidates)) !== null && _a !== void 0 ? _a : undefined;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, d), { snappedTo: rel }) });
                    });
                    // Orphan cleanup: a tile not itself part of this drag/mod set whose
                    // `snappedTo.parentId` points at a tile that DID move only loses the
                    // relationship if it no longer actually touches that tile at its new
                    // (post-drop) position -- e.g. a tile re-dropped back onto the same
                    // spot still touches its other neighbor and must keep that relation.
                    //
                    // This recompute only makes sense when the parent's POSITION changed
                    // (a real drag-away). When the parent only rescaled in place (e.g. a
                    // number-line snap/detach changed its `dimensions` but not its
                    // `center`), `d.center` is still the child's pre-drop position and
                    // comparing it against the parent's already-rescaled dimensions is
                    // an apples-to-oranges edge check -- it produced a `segmentIndex`
                    // wildly outside the valid `[0, denom-1]` range (e.g. -1.67 for a
                    // denom-10 parent), corrupting `snappedTo` for the SNAPPED CHAIN
                    // SCALE/POSITION PROPAGATION pass below, which is the one actually
                    // responsible for moving this child to the parent's new geometry
                    // using the existing (unchanged) relation. So: skip the recompute
                    // entirely when the parent didn't move -- leave `snappedTo` as-is
                    // and let that later pass do its job.
                    _newState_2 = _newState_2.map(function (item) {
                        var _a, _b;
                        if (item.data.type !== 'fractionTiles')
                            return item;
                        var d = item.data;
                        if (movedIds_1.has(d.id) || !d.snappedTo)
                            return item;
                        if (!movedIds_1.has(d.snappedTo.parentId))
                            return item;
                        var parentBefore = (_a = currentManipulative_1.find(function (m) { return m.data.id === d.snappedTo.parentId; })) === null || _a === void 0 ? void 0 : _a.data;
                        var movedParent = (_b = _newState_2.find(function (m) { return m.data.id === d.snappedTo.parentId; })) === null || _b === void 0 ? void 0 : _b.data;
                        var parentPositionChanged = parentBefore &&
                            movedParent &&
                            (Math.abs(parentBefore.center.x - movedParent.center.x) > 0.1 ||
                                Math.abs(parentBefore.center.y - movedParent.center.y) > 0.1);
                        if (!parentPositionChanged)
                            return item;
                        var stillTouching = movedParent && computeSnapRelation(d.center, d.dimensions, movedParent);
                        if (stillTouching)
                            return __assign(__assign({}, item), { data: __assign(__assign({}, d), { snappedTo: stillTouching }) });
                        return __assign(__assign({}, item), { data: __assign(__assign({}, d), { snappedTo: undefined }) });
                    });
                    // Re-establish: an unmoved tile with no (or no longer valid) relation
                    // may now be edge-touching a tile that moved this drop -- e.g. it lost
                    // its relation to that tile on an earlier drag-away and the tile has
                    // since been dropped back into a spot that touches it again. Without
                    // this, only the moved tile's own (single) relation is recomputed, so
                    // a moved tile landing between two neighbors only ever re-links to one
                    // of them and the other's reciprocal relation is never rebuilt.
                    _newState_2 = _newState_2.map(function (item) {
                        var _a;
                        if (item.data.type !== 'fractionTiles')
                            return item;
                        var d = item.data;
                        if (movedIds_1.has(d.id) || d.snappedTo)
                            return item;
                        var movedCandidates = _newState_2
                            .filter(function (m) { var _a; return m.data.type === 'fractionTiles' && movedIds_1.has((_a = m.data.id) !== null && _a !== void 0 ? _a : ''); })
                            .map(function (m) { return m.data; });
                        var rel = (_a = findTouchingParent(d.center, d.dimensions, movedCandidates)) !== null && _a !== void 0 ? _a : undefined;
                        if (!rel)
                            return item;
                        return __assign(__assign({}, item), { data: __assign(__assign({}, d), { snappedTo: rel }) });
                    });
                    _newState_2.forEach(function (item) {
                        var _a, _b;
                        if (item.data.type !== 'fractionTiles')
                            return;
                        var d = item.data;
                        var original = currentManipulative_1.find(function (m) { return m.data.id === d.id; });
                        if (!original)
                            return;
                        var before = (_a = original.data.snappedTo) !== null && _a !== void 0 ? _a : null;
                        var after = (_b = d.snappedTo) !== null && _b !== void 0 ? _b : null;
                        if (JSON.stringify(before) !== JSON.stringify(after)) {
                            dragStateChanges_1.push({
                                type: d.id,
                                id: (d.id || '').split('_')[1],
                                propertyName: 'snappedTo',
                                old: JSON.stringify(before),
                                new: JSON.stringify(after),
                            });
                        }
                    });
                }
                // --- END SNAPPED TO ASSIGNMENT ---
                // --- SNAPPED CHAIN SCALE/POSITION PROPAGATION ---
                // A tile's scale can diverge from its `snappedTo` chain independently
                // of the shared scalePlus/scaleMinus delta by attaching to (or
                // detaching from) a number line, which happened above this drop
                // (see the NL snap-on-drop / preSnapScale-restore branches). Re-walk
                // the whole chain from current `_newState` so every descendant picks
                // up its (possibly just-changed) parent's scale/position, not only
                // the leader tile that actually moved this drop.
                if (shapeType === 'rectangle') {
                    var rectTiles = _newState_2
                        .filter(function (m) { return m.data.type === 'fractionTiles'; })
                        .map(function (m) { return m.data; })
                        .filter(function (d) { return d.shapeType === 'rectangle'; });
                    var resolvedGeometry_1 = resolveSnappedChainGeometry(rectTiles);
                    _newState_2 = _newState_2.map(function (item) {
                        if (item.data.type !== 'fractionTiles')
                            return item;
                        var d = item.data;
                        if (d.shapeType !== 'rectangle')
                            return item;
                        var resolvedFor = resolvedGeometry_1.get(d.id);
                        if (!resolvedFor)
                            return item;
                        var changed = Math.abs(resolvedFor.center.x - d.center.x) > 0.1 ||
                            Math.abs(resolvedFor.center.y - d.center.y) > 0.1 ||
                            Math.abs(resolvedFor.scale - d.scale) > 1e-6 ||
                            resolvedFor.numberLineId !== d.numberLineId;
                        if (!changed)
                            return item;
                        var original = currentManipulative_1.find(function (m) { return m.data.id === d.id; });
                        if (original) {
                            var before = original.data;
                            if (JSON.stringify(before.center) !== JSON.stringify(resolvedFor.center)) {
                                dragStateChanges_1.push({
                                    type: d.id,
                                    id: d.id.split('_')[1],
                                    propertyName: 'center',
                                    old: JSON.stringify(before.center),
                                    new: JSON.stringify(resolvedFor.center),
                                });
                            }
                            if (before.scale !== resolvedFor.scale) {
                                dragStateChanges_1.push({
                                    type: d.id,
                                    id: d.id.split('_')[1],
                                    propertyName: 'scale',
                                    old: JSON.stringify(before.scale),
                                    new: JSON.stringify(resolvedFor.scale),
                                });
                            }
                            if (before.numberLineId !== resolvedFor.numberLineId) {
                                dragStateChanges_1.push({
                                    type: d.id,
                                    id: d.id.split('_')[1],
                                    propertyName: 'numberLineId',
                                    old: JSON.stringify(before.numberLineId),
                                    new: JSON.stringify(resolvedFor.numberLineId),
                                });
                            }
                        }
                        return __assign(__assign({}, item), { data: __assign(__assign({}, d), { center: resolvedFor.center, dimensions: resolvedFor.dimensions, scale: resolvedFor.scale, numberLineId: resolvedFor.numberLineId }) });
                    });
                }
                // --- END SNAPPED CHAIN SCALE/POSITION PROPAGATION ---
                // Bring dragged tiles to the front by moving them to the end of the
                // render array — array order drives paint order. Only committed here,
                // at drop, after a real drag; a plain click never reorders the array
                // (see the CSS zIndex bump above for the click/drag-in-progress case).
                var updatedMap_1 = new Map(_newState_2.map(function (s) { var _a; return [(_a = s.data.id) !== null && _a !== void 0 ? _a : '', s]; }));
                var draggedIds = Object.keys(initialSelectedPositions.current);
                var mergedPrev = manipulativeRef.current.map(function (item) { var _a, _b; return (_b = updatedMap_1.get((_a = item.data.id) !== null && _a !== void 0 ? _a : '')) !== null && _b !== void 0 ? _b : item; });
                var beforeIds = mergedPrev.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var reordered_1 = bringManipulativesToFront(mergedPrev, draggedIds);
                var afterIds = reordered_1.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                if (afterIds.join('|') !== beforeIds.join('|')) {
                    dragStateChanges_1.push({
                        type: id,
                        id: (_d = id.split('_')[1]) !== null && _d !== void 0 ? _d : id,
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(afterIds),
                    });
                }
                if (dragStateChanges_1.length > 0 && !registerMultiSelectSaveEntries(id, dragStateChanges_1)) {
                    saveState(dragStateChanges_1);
                }
                setManipulative(function () { return reordered_1; });
                endMultiDrag();
            }
            // A plain click/tap (no real drag, hasMoved.current stays false) is
            // intentionally a no-op here: the NL-attachment restore now only runs
            // once a real drag is confirmed (see the hasMoved transition in
            // handleMove), so a click never mutates this tile's scale/numberLineId
            // and there is nothing to re-check or save.
            dragStated.current = false;
            dragStartRef.current = null;
            setDragStart(null);
            // Reset hasMoved after the drag-release synthetic click has fired so that
            // future interactions (including VoiceOver double-tap, which fires click
            // without a preceding touchstart that would normally reset this flag)
            // are not blocked by the stale hasMoved=true state.
            setTimeout(function () {
                hasMoved.current = false;
            }, 200);
        };
        if (dragStated.current && dragStart !== null && !panningEnable && !disabled) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', stopDrag);
            window.addEventListener('touchmove', handleMove, { passive: false });
            window.addEventListener('touchend', stopDrag);
            window.addEventListener('touchcancel', stopDrag);
        }
        return function () {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', stopDrag);
            window.removeEventListener('touchmove', handleMove);
            window.removeEventListener('touchend', stopDrag);
            window.removeEventListener('touchcancel', stopDrag);
            setSnapPreview(null);
        };
    }, [dragStart, isFocused, panningEnable, disabled, shapeType, playerBgPattern]);
    /** ---------------- MULTI-SELECT FOLLOWER (different-type leader) ---------------- */
    // Follow a DIFFERENT-type leader (e.g. Dice, a card deck) dragging alongside this tile in
    // the current multi-select. Following ANOTHER fractionTile leader is already handled by this
    // file's own initialSelectedPositions system in onMouseDown/handleMove — this effect only
    // needs to fire for a source of some other type (fractionTiles is excluded from the central
    // engine's own follower handling; see SELF_MANAGED_TYPES in multiDragHelper.ts).
    useEffect(function () {
        // Grouped tiles carry the group's token (`manipulativeGroup_...`) in
        // `selectedTool` instead of their own `id` (see `isFocused` above) — check
        // both so a grouped tile still follows a different-type leader.
        var isSelected = function (tool) {
            return tool.includes(id) || (!!groupId && tool.includes(groupId));
        };
        var isMultiSelected = isSelected(selectedTool) && selectedTool.length > 1;
        if (!isMultiSelected)
            return;
        var myStart = { x: tilePosRef.current.x, y: tilePosRef.current.y };
        var lastAppliedCenter = null;
        // A leader that is itself a fractionTile (grouped or not) broadcasts with its own bare
        // `id` when ungrouped, or its `groupId` when grouped (see the dragKey change above) — either
        // way, that leader's own handleMove/stopDrag already moves and batch-commits every member of
        // ITS group (or itself) directly via initialSelectedPositions, so this tile must not also
        // apply the generic follower delta on top of that when the source is its own group.
        var isOwnFractionTileSource = function (sourceId) { return sourceId === id || sourceId === groupId; };
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (isOwnFractionTileSource(sourceId))
                return;
            myStart = { x: tilePosRef.current.x, y: tilePosRef.current.y };
            lastAppliedCenter = null;
        };
        var onMove = function (_a) {
            var sourceId = _a.sourceId, deltaX = _a.deltaX, deltaY = _a.deltaY;
            if (isOwnFractionTileSource(sourceId) || positionLock)
                return;
            var inMultiSel = isSelected(selectedToolRef.current) && selectedToolRef.current.length > 1;
            if (!inMultiSel)
                return;
            var newCenter = { x: myStart.x + deltaX, y: myStart.y + deltaY };
            lastAppliedCenter = newCenter;
            setTilePosition(newCenter);
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter }) }) : item;
                });
            });
        };
        var onEnd = function (_a) {
            var sourceId = _a.sourceId;
            if (isOwnFractionTileSource(sourceId))
                return;
            if (!lastAppliedCenter)
                return;
            // A fractionTiles leader already batch-commits every follower's final center in its
            // own onUp/initialSelectedPositions system — only self-save when the leader is some
            // OTHER type (Dice, a card deck, Shades, ...), same guard ColoredTiles.tsx uses.
            var sourceIsFractionTileGroup = manipulativeRef.current.some(function (m) { return m.data.type === 'fractionTiles' && m.data.groupId === sourceId; });
            var sourceItem = manipulativeRef.current.find(function (m) { return m.data.id === sourceId; });
            if ((sourceItem === null || sourceItem === void 0 ? void 0 : sourceItem.data.type) === 'fractionTiles' || sourceIsFractionTileGroup) {
                lastAppliedCenter = null;
                return;
            }
            var oldCenter = myStart;
            var newCenter = lastAppliedCenter;
            lastAppliedCenter = null;
            var entry = {
                type: id,
                id: id.split('_')[1],
                propertyName: 'center',
                old: JSON.stringify(oldCenter),
                new: JSON.stringify(newCenter),
            };
            if (!registerMultiSelectSaveEntries(sourceId, [entry])) {
                saveState(entry);
            }
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        customEventHandler.addEventListener('multiSelectMoveEnd', onEnd);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
            customEventHandler.removeEventListener('multiSelectMoveEnd', onEnd);
        };
    }, [selectedTool, id, groupId, positionLock, setManipulative]);
    /** ---------------- SYNC TILE SCALE ---------------- */
    useEffect(function () {
        setTileScale(scale);
    }, [scale]);
    /** ---------------- RECALCULATE DIMENSION WHEN SCALE ---------------- */
    // Derives from the `scale` PROP (the store's authoritative value), not the
    // local `tileScale` mirror — `tileScale` is one render behind `scale` right
    // after it changes (its own sync effect above hasn't committed yet), so
    // using it here to write back to the store created a self-sustaining
    // oscillator: every incoming `scale` change got echoed back as the
    // *previous* value, which the next render then echoed back again, forever.
    // This effect only ever needs to keep `dimensions` consistent with `scale`;
    // it must never write `scale` itself back, since `scale` isn't derived here.
    useEffect(function () {
        var isGlued = gluedFrom && gluedFrom.length > 0;
        var baseWidth = shapeType === 'circle' && !isGlued ? 300 : defaultDimension.width;
        var baseHeight = shapeType === 'circle' && !isGlued ? 300 : defaultDimension.height;
        var newWidth = baseWidth * scale;
        var newHeight = baseHeight * scale;
        setTileSize({ width: newWidth, height: newHeight });
        setManipulative(function (prev) {
            var _a;
            var current = (_a = prev.find(function (item) { return item.data.id === id; })) === null || _a === void 0 ? void 0 : _a.data;
            var FLOAT_EPS = 1e-6;
            if (current &&
                Math.abs(current.dimensions.width - newWidth) < FLOAT_EPS &&
                Math.abs(current.dimensions.height - newHeight) < FLOAT_EPS) {
                return prev;
            }
            return prev.map(function (item) {
                return item.data.id === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { dimensions: { width: newWidth, height: newHeight } }) }) : item;
            });
        });
    }, [scale, shapeType, defaultDimension.width, defaultDimension.height, (_a = gluedFrom === null || gluedFrom === void 0 ? void 0 : gluedFrom.length) !== null && _a !== void 0 ? _a : 0]);
    /** ---------------- SYNC TILE DIMENSION ---------------- */
    useEffect(function () {
        setTileSize(dimensions);
    }, [JSON.stringify(dimensions)]);
    /** ---------------- SYNC TILE DIMENSION REF ---------------- */
    useEffect(function () {
        tilePosRef.current = tilePosition;
    }, [tilePosition]);
    /** ---------------- SYNC TILE POSITION WITH PROP ---------------- */
    // useLayoutEffect (not useEffect): this mirrors the `center` prop into local
    // `tilePosition` render state. During a multi-select drag, followers receive
    // new `center` values on every leader mousemove — with a passive useEffect,
    // the browser paints the OLD tilePosition first and only corrects it on the
    // NEXT frame once the effect fires, so followers visibly lag the leader by a
    // frame. useLayoutEffect applies the correction synchronously before paint.
    useLayoutEffect(function () {
        if (!dragStated.current) {
            setTilePosition(center);
        }
    }, [center.x, center.y]);
    /** ---------------- BOTTOM PANEL EVENT ---------------- */
    useEffect(function () {
        var changeColour = function (data) {
            var _oldColor;
            var _newColor = data.color;
            var currentManipulative = manipulativeRef.current;
            var currentItem = currentManipulative.find(function (item) { return item.data.id === id; });
            if (currentItem) {
                _oldColor = currentItem.data.color;
            }
            if (_oldColor !== _newColor) {
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'color',
                    old: JSON.stringify(_oldColor || ''),
                    new: JSON.stringify(_newColor),
                });
            }
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    if (item.data.id === id) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { color: _newColor }) }); // ensure branch type
                    }
                    return item;
                });
            });
        };
        var onToolClick = function (data) {
            switch (data.id) {
                case 'edit':
                    setActiveManipulator('fractionTiles');
                    setManiplulatorComponentVisible(true);
                    setSelectedManipulativeId(id);
                    break;
                case 'focusCustomLabel': {
                    if (lablesType !== 'custom') {
                        setManipulative(function (prev) {
                            return prev.map(function (item) {
                                return item.data.id === id
                                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { lablesType: 'custom' }) }) : item;
                            });
                        });
                    }
                    setIsEditing(true);
                    // Also narrow the selectedTool to the custom-label variant so
                    // VoiceOver sees the input as the active element after navigation.
                    var labelSuffix = shapeType === 'circle' ? "".concat(id, "_custom_label_circle_1") : "".concat(id, "_custom_label_rect_1");
                    setSelectedTool([labelSuffix]);
                    // Double-rAF: waits for React to commit the DOM (wrapper mounts after
                    // lablesType changes) while staying within iOS's gesture window so the
                    // soft keyboard is allowed to appear.
                    requestAnimationFrame(function () {
                        requestAnimationFrame(function () {
                            var wrapper = shapeType === 'circle' ? labelWrapperCircleRef.current : labelWrapperRef.current;
                            var input = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('[contenteditable], math-field, input');
                            if (input) {
                                input.focus({ preventScroll: true });
                            }
                            else if (mathEditorRef.current) {
                                mathEditorRef.current.focus();
                            }
                        });
                    });
                    break;
                }
            }
        };
        if (isFocused) {
            customEventHandler.addEventListener('bottomPanel', onToolClick);
            customEventHandler.addEventListener('colourChange', changeColour);
            return function () {
                customEventHandler.removeEventListener('bottomPanel', onToolClick);
                customEventHandler.removeEventListener('colourChange', changeColour);
            };
        }
    }, [isFocused]);
    var currentFractionSize = useMemo(function () {
        return gluedFrom && gluedFrom.length > 0
            ? sumAndReduceFractions(gluedFrom.map(function (f) { return f.fractionSize; }))
            : fractionSize;
    }, [fractionSize, gluedFrom]);
    var isSmallTile = useMemo(function () {
        if (shapeType === 'rectangle') {
            return tileSize.width < 32;
        }
        else {
            var config = getFractionCircleConfig(currentFractionSize, rotation !== null && rotation !== void 0 ? rotation : 0, customSweep);
            return config.sweep < 20;
        }
    }, [shapeType, tileSize.width, currentFractionSize, rotation, customSweep]);
    // All rect tiles have border-right: none (so all interiors are the same width).
    // Tiles with no right neighbor get a box-shadow right edge instead, which draws
    // the border visually without affecting box-sizing or interior content width.
    // While THIS tile is being dragged, always show the right border (hasRightNeighbor = false)
    // so the dragged tile never appears borderless.
    // hasRightNeighbor kept for potential future use; currently unused since box-shadow
    // is applied unconditionally in CSS making adjacent borders overlap cleanly.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    var hasRightNeighbor = useMemo(function () {
        // Dragged tile: always show right border while in motion
        if (dragStart !== null)
            return false;
        if (shapeType !== 'rectangle')
            return false;
        var myRight = center.x + tileSize.width;
        var TOL = 5;
        // Exclude tiles that are currently selected (being dragged) — they remain in
        // manipulative at their old position, but are visually elsewhere.
        var draggingIds = new Set(selectedTool.map(function (t) { return t.split(':')[0]; }));
        return manipulative.some(function (m) {
            if (m.data.id === id || m.data.type !== 'fractionTiles')
                return false;
            if (draggingIds.has(m.data.id))
                return false;
            var d = m.data;
            if (d.shapeType !== 'rectangle')
                return false;
            if (d.center == null)
                return false;
            if (Math.abs(d.center.y - center.y) > 10)
                return false;
            return d.center.x >= myRight - TOL && d.center.x <= myRight + TOL;
        });
    }, [manipulative, center.x, center.y, tileSize.width, shapeType, id, dragStart, selectedTool]);
    var maxChars = useMemo(function () {
        var _a, _b, _c, _d;
        if (lablesType !== 'custom')
            return undefined;
        var explicit = (_a = FRACTION_MAX_CHARS[shapeType]) === null || _a === void 0 ? void 0 : _a[currentFractionSize];
        if (explicit !== undefined)
            return explicit;
        // For split tiles with non-standard fractionSize (e.g. fraction-1-7, fraction-1-9),
        // derive a limit from the sector's sweep angle so long text is still capped.
        if (shapeType === 'circle') {
            var sweep = (_d = customSweep !== null && customSweep !== void 0 ? customSweep : (_c = (_b = getFractionDimensions(currentFractionSize)) === null || _b === void 0 ? void 0 : _b.circle) === null || _c === void 0 ? void 0 : _c.sweep) !== null && _d !== void 0 ? _d : 360;
            if (sweep >= 180)
                return 12;
            if (sweep >= 90)
                return 6;
            if (sweep >= 60)
                return 4;
            if (sweep >= 45)
                return 3;
            if (sweep >= 30)
                return 2;
            return 2;
        }
        return undefined;
    }, [lablesType, shapeType, currentFractionSize, customSweep]);
    var labelTitle = useMemo(function () {
        if (lablesType === 'custom') {
            if (Array.isArray(customLabel)) {
                return customLabel
                    .map(function (item) { return item.value || item.latex || ''; })
                    .join(' ')
                    .trim();
            }
            return String(customLabel || '');
        }
        // For glued tiles, show the combined fraction
        if (customNumerator != null && customDenominator != null) {
            if (customNumerator === customDenominator && customDenominator !== 0) {
                return '1';
            }
            return (_jsx(Fraction, { integer: customDenominator === 0 ? customNumerator : undefined, numerator: customNumerator, denominator: customDenominator }));
        }
        var parsed = parseFraction(currentFractionSize);
        if (parsed) {
            if (parsed.numerator === parsed.denominator && parsed.denominator !== 0) {
                return '1';
            }
            return (_jsx(Fraction, { integer: parsed.denominator === 0 ? parsed.numerator : undefined, numerator: parsed.numerator, denominator: parsed.denominator }));
        }
        return null;
    }, [
        lablesType,
        customLabel,
        currentFractionSize,
        customNumerator,
        customDenominator,
        shapeType,
        rotation,
    ]);
    /** ---------------- INPUT HANDLER ---------------- */
    // MathEditor handles its own state, but we need to push changes to the manipulative store
    var handleMathEditorChange = function (val) {
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id
                    ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { customLabel: val }) })
                    : item;
            });
        });
    };
    var bb = useMemo(function () { return getCircleBoundingBox(currentFractionSize, rotation !== null && rotation !== void 0 ? rotation : 0, tileScale, customSweep); }, [currentFractionSize, rotation, tileScale, customSweep]);
    var clipPath = useMemo(function () {
        return shapeType === 'circle'
            ? getCircleClipPath(currentFractionSize, rotation !== null && rotation !== void 0 ? rotation : 0, customSweep)
            : undefined;
    }, [currentFractionSize, rotation, customSweep, shapeType]);
    var circleLabelOverlay = useMemo(function () {
        if (shapeType !== 'circle')
            return null;
        var config = getFractionCircleConfig(currentFractionSize, rotation !== null && rotation !== void 0 ? rotation : 0, customSweep);
        var radius = 150; // Half of 300
        var svgCenter = 150;
        var isFullCircle = config.sweep === 360;
        var showLabel = lablesType === 'custom' || (tileSize.width >= 32 && config.sweep >= 20);
        if (!showLabel)
            return null;
        // Calculate label position (centroid of sector or center of circle)
        var midAngle = config.start + config.sweep / 2;
        var isGlued = !!(gluedFrom && gluedFrom.length > 0);
        var labelRadius = 0;
        if (!isFullCircle) {
            var alpha = (config.sweep / 2) * (Math.PI / 180);
            var safeAlpha = alpha === 0 ? 1 : alpha;
            labelRadius = (2 * radius * Math.sin(safeAlpha)) / (3 * safeAlpha);
            // For glued (combined) tiles with sweep > 180° the geometric centroid is very
            // close to the circle centre, making the label appear "stuck in the middle".
            // Push it to at least 40% of the radius so it sits visibly within the shape.
            if (isGlued && config.sweep > 180) {
                labelRadius = Math.max(radius * 0.4, labelRadius);
            }
        }
        var labelPos = polarToCartesian(svgCenter, svgCenter, labelRadius, midAngle);
        // Calculate dynamic width for circle label based on chord width at label position
        var alphaChord = (config.sweep / 2) * (Math.PI / 180);
        var chord = 2 * labelRadius * Math.sin(alphaChord);
        var sectorMaxWidth = isFullCircle ? 240 : chord * 0.95;
        var circleLabelWidth = Math.max(20, sectorMaxWidth);
        if (isGlued) {
            // For large-sweep glued shapes the label sits further from centre, so give
            // it a generous minimum width that scales with how much of the circle it covers.
            var gluedMinWidth = config.sweep > 180 ? Math.max(100, (config.sweep / 360) * 2 * radius * 0.7) : 60;
            circleLabelWidth = Math.max(gluedMinWidth, circleLabelWidth);
        }
        var absoluteTextAngle = (midAngle + 90) % 360;
        if (absoluteTextAngle < 0)
            absoluteTextAngle += 360;
        var finalRotation = absoluteTextAngle;
        var roundedAngle = Math.round(absoluteTextAngle);
        if (roundedAngle > 90 && roundedAngle < 270) {
            finalRotation += 180;
        }
        var parsed = parseFraction(currentFractionSize);
        if (currentFractionSize === 'fraction-1' ||
            isFullCircle ||
            (parsed && parsed.denominator === 0) ||
            isGlued) {
            finalRotation = 0;
        }
        // The SVG viewBox spans from bb.x/bb.y to bb.x+bb.width / bb.y+bb.height
        // We need to convert the SVG-space labelPos (in 0-300 coordinates) to
        // a percentage relative to the rendered container (bb coordinates).
        var svgViewBoxWidth = bb.width;
        var svgViewBoxHeight = bb.height;
        var labelXPct = ((labelPos.x - bb.x) / svgViewBoxWidth) * 100;
        var labelYPct = ((labelPos.y - bb.y) / svgViewBoxHeight) * 100;
        // Convert circleLabelWidth from SVG-space to a percentage of the container
        var labelWidthPct = (circleLabelWidth / svgViewBoxWidth) * 100;
        var fontSize = !!FRACTION_CONFIG[currentFractionSize]
            ? "".concat(1.563 * tileScale, "rem")
            : "".concat((1 + 0.5625 * Math.min(1, Math.max(0, (config.sweep - 30) / (180 - 30)))) * tileScale, "rem");
        return {
            labelXPct: labelXPct,
            labelYPct: labelYPct,
            labelWidthPct: labelWidthPct,
            finalRotation: lablesType === 'labeled' ? finalRotation : 0,
            fontSize: fontSize,
        };
    }, [
        shapeType,
        currentFractionSize,
        rotation,
        customSweep,
        lablesType,
        tileSize.width,
        tileScale,
        bb,
    ]);
    var renderCircleShape = function () {
        var config = getFractionCircleConfig(currentFractionSize, rotation !== null && rotation !== void 0 ? rotation : 0, customSweep);
        var radius = 150;
        var svgCenter = 150;
        var isFullCircle = config.sweep === 360;
        var commonProps = {
            fill: toShadeColor(color),
            className: 'fraction-tile-shape',
            style: { '--tile-color': toShadeColor(color) },
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
        };
        var pathD = isFullCircle
            ? ''
            : describeArc(svgCenter, svgCenter, radius, config.start, config.start + config.sweep);
        return isFullCircle ? (_jsx("circle", __assign({ cx: svgCenter, cy: svgCenter, r: radius }, commonProps))) : (_jsx("path", __assign({ d: pathD }, commonProps)));
    };
    return (_jsxs(_Fragment, { children: [snapPreview && (_jsx("div", { className: "fraction-tile-manipulative-wrapper fraction-tile-manipulative fraction-tile-manipulative--ghost ".concat(shapeType === 'circle'
                    ? 'fraction-tile-manipulative--circle'
                    : 'fraction-tile-manipulative--rect'), style: {
                    top: snapPreview.y,
                    left: snapPreview.x,
                }, children: ((_b = snapPreview.group) !== null && _b !== void 0 ? _b : [{ offset: { x: 0, y: 0 }, dimensions: tileSize, id: id }]).map(function (item, idx) {
                    var _a, _b, _c, _d, _e;
                    var m = manipulativeRef.current.find(function (manip) { return manip.data.id === item.id; });
                    if (!m)
                        return null;
                    var d = m.data;
                    var isLeader = item.id === id;
                    if (d.shapeType === 'rectangle') {
                        return (_jsx("div", { className: "fraction-tile-manipulative-rect-inner", style: {
                                left: item.offset.x,
                                top: item.offset.y,
                                width: item.dimensions.width,
                                height: item.dimensions.height,
                                '--tile-color': toShadeColor(d.color),
                            } }, item.id));
                    }
                    else {
                        var itemRotation = isLeader
                            ? ((_b = (_a = snapPreview.rotation) !== null && _a !== void 0 ? _a : d.rotation) !== null && _b !== void 0 ? _b : 0)
                            : ((_c = d.rotation) !== null && _c !== void 0 ? _c : 0);
                        var scaleFactor = (_d = d.scale) !== null && _d !== void 0 ? _d : 1;
                        var bb_1 = getCircleBoundingBox(d.fractionSize, itemRotation, scaleFactor, d.customSweep);
                        var itemConfig_1;
                        if (d.customSweep != null) {
                            itemConfig_1 = { start: itemRotation % 360, sweep: d.customSweep };
                        }
                        else {
                            var baseConfig = ((_e = getFractionDimensions(d.fractionSize)) === null || _e === void 0 ? void 0 : _e.circle) || {
                                start: 0,
                                sweep: 360,
                            };
                            itemConfig_1 = {
                                start: (baseConfig.start + itemRotation) % 360,
                                sweep: baseConfig.sweep,
                            };
                        }
                        return (_jsx("div", { className: "fraction-tile-manipulative-circle-container", style: {
                                left: item.offset.x + bb_1.x * scaleFactor,
                                top: item.offset.y + bb_1.y * scaleFactor,
                                width: bb_1.width * scaleFactor,
                                height: bb_1.height * scaleFactor,
                            }, children: _jsx("svg", { width: "100%", height: "100%", viewBox: "".concat(bb_1.x, " ").concat(bb_1.y, " ").concat(bb_1.width, " ").concat(bb_1.height), className: "fraction-tile-manipulative-svg", children: (function () {
                                    var radius = 150;
                                    var center = 150;
                                    var isFull = itemConfig_1.sweep === 360;
                                    var common = {
                                        fill: toShadeColor(d.color),
                                        stroke: '#293273',
                                        strokeWidth: '5',
                                        strokeDasharray: '5,5',
                                    };
                                    return isFull ? (_jsx("circle", { cx: center, cy: center, r: radius, className: "fraction-tile-shape", style: { '--tile-color': toShadeColor(d.color) } })) : (_jsx("path", { d: describeArc(center, center, radius, itemConfig_1.start, itemConfig_1.start + itemConfig_1.sweep), className: "fraction-tile-shape", style: { '--tile-color': toShadeColor(d.color) } }));
                                })() }) }, item.id));
                    }
                }) })), (_c = snapPreview === null || snapPreview === void 0 ? void 0 : snapPreview.modifications) === null || _c === void 0 ? void 0 : _c.map(function (mod) {
                var m = manipulativeRef.current.find(function (manip) { return manip.data.id === mod.id; });
                if (!m)
                    return null;
                var d = m.data;
                if (d.shapeType !== 'rectangle')
                    return null;
                return (_jsx("div", { className: "fraction-tile-manipulative-wrapper fraction-tile-manipulative fraction-tile-manipulative--ghost fraction-tile-manipulative--rect", style: { top: mod.center.y, left: mod.center.x }, children: _jsx("div", { className: "fraction-tile-manipulative-rect-inner", style: {
                            width: d.dimensions.width,
                            height: d.dimensions.height,
                            '--tile-color': toShadeColor(d.color),
                        } }) }, "mod-ghost-".concat(mod.id)));
            }), _jsxs("div", { ref: tileInteractiveRef, id: id, "data-manipulative-id": id, className: "fraction-tile-manipulative-wrapper fraction-tile-manipulative ".concat(shapeType === 'circle'
                    ? 'fraction-tile-manipulative--circle'
                    : 'fraction-tile-manipulative--rect', " ").concat(isFocused ? 'fraction-tile-manipulative--focused' : '', " ").concat(disabled
                    ? 'fraction-tile-manipulative--disabled'
                    : positionLock
                        ? 'fraction-tile-manipulative--position-lock'
                        : dragStated.current
                            ? 'fraction-tile-manipulative--dragging'
                            : 'fraction-tile-manipulative--pointer', " ").concat(dragStated.current && snapPreview ? 'fraction-tile-manipulative--drag-source-hidden' : ''), style: {
                    top: tilePosition.y + (shapeType === 'circle' ? bb.y * tileScale : 0),
                    left: tilePosition.x + (shapeType === 'circle' ? bb.x * tileScale : 0),
                    zIndex: dragStated.current ? 9999 : undefined,
                    pointerEvents: shapeType === 'circle' ? 'none' : undefined,
                    width: shapeType === 'circle' ? bb.width * tileScale : undefined,
                    height: shapeType === 'circle' ? bb.height * tileScale : undefined,
                }, onMouseDown: onMouseDown, onTouchStart: onMouseDown, onTouchEnd: IS_TOUCH_DEVICE
                    ? function (e) {
                        var _a, _b, _c, _d, _e, _f;
                        if (hasMoved.current)
                            return;
                        var touchTarget = e.target;
                        // Math-keypad icon: call insertMath() directly from touchend
                        // instead of relying on the synthesised click event.  On iPad the
                        // click can be swallowed when the button sits inside a
                        // pointer-events:none container (circle tiles) or when iOS
                        // decides not to synthesise the click after certain touch
                        // sequences.  Calling from touchend is always reliable.
                        if ((_a = touchTarget.closest) === null || _a === void 0 ? void 0 : _a.call(touchTarget, '.fraction-tile-math-btn')) {
                            (_b = mathEditorRef.current) === null || _b === void 0 ? void 0 : _b.insertMath();
                            return;
                        }
                        if (((_c = touchTarget.closest) === null || _c === void 0 ? void 0 : _c.call(touchTarget, '.keypadWrapper')) ||
                            ((_d = touchTarget.closest) === null || _d === void 0 ? void 0 : _d.call(touchTarget, '.ML__keyboard')) ||
                            ((_e = touchTarget.closest) === null || _e === void 0 ? void 0 : _e.call(touchTarget, '[part="keyboard"]')))
                            return;
                        // Circle tiles have pointer-events:none on their outer wrapper,
                        // so VoiceOver's synthesised click may not fire.  Open the
                        // context menu from onTouchEnd instead (touch events bubble
                        // through pointer-events:none wrappers reliably).
                        if (shapeType === 'circle' && lastFocusWasScreenReader.current) {
                            lastFocusWasScreenReader.current = false;
                            if (!selectedTool.includes(id))
                                setSelectedTool([id]);
                            var rect = (_f = tileInteractiveRef.current) === null || _f === void 0 ? void 0 : _f.getBoundingClientRect();
                            if (rect)
                                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                            return;
                        }
                        voiceFocusRef.current = false;
                    }
                    : undefined, onClick: IS_TOUCH_DEVICE
                    ? function (e) {
                        var _a, _b, _c, _d, _e;
                        if (hasMoved.current)
                            return;
                        var clickTarget = e.target;
                        if (((_a = clickTarget.closest) === null || _a === void 0 ? void 0 : _a.call(clickTarget, '.fraction-tile-math-btn')) ||
                            ((_b = clickTarget.closest) === null || _b === void 0 ? void 0 : _b.call(clickTarget, '.keypadWrapper')) ||
                            ((_c = clickTarget.closest) === null || _c === void 0 ? void 0 : _c.call(clickTarget, '.ML__keyboard')) ||
                            ((_d = clickTarget.closest) === null || _d === void 0 ? void 0 : _d.call(clickTarget, '[part="keyboard"]')))
                            return;
                        // Mirror SpinnerManipulative exactly:
                        // VoiceOver sets :focus-visible before synthesising a click;
                        // regular touch does not. No timing guard needed — the flag is
                        // sufficient to distinguish VoiceOver activation from tap.
                        if (!IS_TOUCH_DEVICE || !lastFocusWasScreenReader.current)
                            return;
                        e.preventDefault();
                        lastFocusWasScreenReader.current = false;
                        if (!selectedTool.includes(id))
                            setSelectedTool([id]);
                        var rect = (_e = tileInteractiveRef.current) === null || _e === void 0 ? void 0 : _e.getBoundingClientRect();
                        if (rect)
                            setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                    }
                    : undefined, tabIndex: disabled || groupId ? -1 : 0, onFocus: function (e) {
                    var _a, _b, _c;
                    if (pointerDownRef.current) {
                        // Pointer-driven focus (touch/mouse): preserve lastFocusWasScreenReader
                        // set by the preceding VoiceOver swipe — do NOT overwrite it here.
                        // During VoiceOver double-tap the inner textbox steals focus, causing
                        // this handler to fire again; overwriting would clear the flag and
                        // prevent onClick from opening the context menu.
                        pointerDownRef.current = false;
                        return;
                    }
                    // Non-pointer focus (VoiceOver swipe or keyboard Tab):
                    // detect whether a screen reader triggered this focus event.
                    try {
                        lastFocusWasScreenReader.current = !!((_b = (_a = e.target).matches) === null || _b === void 0 ? void 0 : _b.call(_a, ':focus-visible'));
                    }
                    catch (_d) {
                        lastFocusWasScreenReader.current = false;
                    }
                    voiceFocusRef.current = true;
                    if (!disabled) {
                        // Only update selection when focus arrives from OUTSIDE the tile.
                        // Internal focus movements (e.g. editorDiv → math-field inserted
                        // by insertMath) also bubble here.  If we call setSelectedTool for
                        // those, selectedTool changes from [id_custom_label_rect_1] to [id],
                        // wasFocused becomes false, and the selectedTool useEffect schedules
                        // tileInteractiveRef.focus() after 100 ms — stealing focus from
                        // the math-field and closing the MathLive keyboard.
                        var relatedTarget = e.relatedTarget;
                        var focusCameFromInside = !!((_c = tileInteractiveRef.current) === null || _c === void 0 ? void 0 : _c.contains(relatedTarget));
                        // iPad: during iOS focus oscillation around a freshly-inserted
                        // math-field (math-field → BODY → math-field re-focus), the second
                        // focusin's relatedTarget is null even though focus never really
                        // left the tile. e.target reliably tells us what's gaining focus —
                        // if it's an inner element (math-field, editorDiv) and not the tile
                        // wrapper itself, this is an internal shift; don't reset selection.
                        var targetIsTileWrapper = e.target === tileInteractiveRef.current;
                        if (!focusCameFromInside && targetIsTileWrapper) {
                            setSelectedTool([id]);
                            setSelectedManipulativeId(id);
                        }
                    }
                }, onBlur: function (e) {
                    var _a;
                    // Only reset when focus truly leaves this tile — not when it moves
                    // to an inner element (e.g. label input on VoiceOver double-tap).
                    var relatedTarget = e.relatedTarget;
                    if ((_a = tileInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.contains(relatedTarget))
                        return;
                    // For circle tiles, VoiceOver's double-tap sends a touchstart to the
                    // transparent SVG rect (which can't hold DOM focus), so the browser
                    // fires blur with relatedTarget=null mid-sequence — before onTouchEnd
                    // fires.  If pointerDownRef is still set the touch is still in
                    // flight; preserve the flag so the context-menu check in onTouchEnd
                    // still sees it.
                    if (pointerDownRef.current)
                        return;
                    lastFocusWasScreenReader.current = false;
                }, role: "button", "aria-roledescription": t('text.draggableFractionTile', {
                    defaultValue: 'draggable fraction tile',
                }), "aria-label": t('text.fractionTileAriaLabel', {
                    title: (function () {
                        if (lablesType === 'custom')
                            return getLabelAriaText(customLabel);
                        if (customNumerator != null && customDenominator != null) {
                            return getFractionAriaText(customNumerator, customDenominator);
                        }
                        var parsed = parseFraction(currentFractionSize);
                        if (parsed)
                            return getFractionAriaText(parsed.numerator, parsed.denominator);
                        return t('text.fractionTile', { defaultValue: 'Fraction tile' });
                    })(),
                    shapeType: t("text.".concat(shapeType), { defaultValue: shapeType }),
                    x: Math.round(tilePosition.x),
                    y: Math.round(tilePosition.y),
                    defaultValue: '{{title}} {{shapeType}}, at position {{x}}, {{y}}. Use arrow keys to move, delete key to remove.',
                }), children: [IS_TOUCH_DEVICE && (lablesType === 'custom' || shapeType === 'circle') && (_jsx(SelectFocusOverlay, { id: id, hidden: disabled, className: shapeType === 'circle'
                            ? 'fraction-tile-context-btn fraction-tile-context-btn--circle-vo'
                            : 'fraction-tile-context-btn', title: t('text.openMenu', { defaultValue: 'Open menu' }), showToolTip: false, onAfterClick: function () {
                            var _a;
                            var rect = (_a = tileInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                            if (rect)
                                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                        } })), shapeType === 'rectangle' ? (_jsxs("div", { className: "fraction-tile-manipulative-rect-inner", style: {
                            width: dragStart !== null ? Math.round(tileSize.width) : tileSize.width,
                            height: dragStart !== null ? Math.round(tileSize.height) : tileSize.height,
                            '--tile-color': toShadeColor(color),
                        }, children: [((lablesType === 'custom' && !isSmallTile) || tileSize.width >= 32) &&
                                (lablesType === 'custom' && !isSmallTile ? (_jsx("div", { ref: labelWrapperRef, className: "fraction-tile-manipulative-label-input-wrapper", style: {
                                        '--tile-height': "".concat(tileSize.height, "px"),
                                        '--tile-scale': tileScale,
                                        '--tile-width': "".concat(tileSize.width, "px"),
                                        pointerEvents: editingLock ? 'none' : undefined,
                                    }, onMouseDownCapture: onMouseDown, onTouchStartCapture: onMouseDown, onPointerDownCapture: function (e) {
                                        var _a, _b;
                                        var alreadyFocused = !!((_a = labelWrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement));
                                        wasEditingAtPointerRef.current = isEditing;
                                        // When the editor is already focused, stop propagation so the
                                        // MathEditor onPointerDown (Firefox first-click fix: blur+focus) doesn't
                                        // run — which would reset the cursor position away from the click point.
                                        // Skip for math-field clicks — MathLive needs pointerdown to handle
                                        // internal cursor positioning (e.g. clicking fraction placeholders).
                                        // Math-field's own handler already calls stopPropagation.
                                        var target = e.target;
                                        var isMathFieldClick = target.tagName === 'MATH-FIELD' || !!((_b = target.closest) === null || _b === void 0 ? void 0 : _b.call(target, 'math-field'));
                                        if (isEditing && alreadyFocused && !isMathFieldClick) {
                                            e.stopPropagation();
                                        }
                                    }, onFocus: function (e) {
                                        if (!editingLock) {
                                            if (!pointerDownRef.current) {
                                                // Non-pointer focus: VoiceOver swipe to this input (req #2)
                                                // or keyboard Tab — always enter editing mode.
                                                setIsEditing(true);
                                                if (selectedTool.length > 1) {
                                                    setSelectedTool(["".concat(id, "_custom_label_rect_1")]);
                                                }
                                            }
                                            else if (!lastFocusWasScreenReader.current) {
                                                // Pointer-driven focus from a non-VoiceOver source (regular
                                                // mouse/touch click) — enter editing mode.
                                                setIsEditing(true);
                                                if (selectedTool.length > 1) {
                                                    setSelectedTool(["".concat(id, "_custom_label_rect_1")]);
                                                }
                                            }
                                            // else: pointer-driven AND lastFocusWasScreenReader=true
                                            // → VoiceOver double-tap side-effect; the outer tile's onClick
                                            // will open the context menu — don't enter editing mode here.
                                            if (!isFocused) {
                                                setSelectedTool(["".concat(id, "_custom_label_rect_1")]);
                                            }
                                        }
                                        if (!pointerDownRef.current) {
                                            voiceFocusRef.current = true;
                                        }
                                    }, onKeyDown: function (e) {
                                        var _a;
                                        // Safari contenteditable swallows Tab and doesn't navigate.
                                        // Explicitly move focus to the math button when Tab is pressed.
                                        if (e.key !== 'Tab' || e.shiftKey)
                                            return;
                                        var mathBtn = (_a = tileInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.fraction-tile-math-btn');
                                        if (!mathBtn)
                                            return;
                                        e.preventDefault();
                                        mathBtn.focus();
                                    }, onClick: function (e) {
                                        var _a, _b, _c;
                                        // Suppress when context menu was opened via onTouchEnd (VoiceOver or
                                        // regular double-tap) so the click that follows doesn't activate the editor.
                                        if (suppressNextLabelClick.current) {
                                            suppressNextLabelClick.current = false;
                                            return;
                                        }
                                        // VoiceOver double-tap on the outer tile bubble here too;
                                        // let the tile's onClick open the context menu instead.
                                        if (IS_TOUCH_DEVICE && lastFocusWasScreenReader.current)
                                            return;
                                        if (voiceFocusRef.current)
                                            return;
                                        var target = e.target;
                                        if (target.closest('.keypadWrapper') ||
                                            target.closest('.fraction-tile-math-btn'))
                                            return;
                                        var alreadyFocused = !!((_a = labelWrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement));
                                        if (!editingLock && !disabled) {
                                            setIsEditing(true);
                                            var wasEditing = wasEditingAtPointerRef.current;
                                            if (!alreadyFocused) {
                                                // Editor not yet focused at all — capture range from current layout
                                                // (range is node+offset based so stays valid after layout change)
                                                var clickX_1 = e.clientX;
                                                var clickY_1 = e.clientY;
                                                var preRange_1 = (_c = (_b = document.caretRangeFromPoint) === null || _b === void 0 ? void 0 : _b.call(document, clickX_1, clickY_1)) !== null && _c !== void 0 ? _c : (function () {
                                                    var _a, _b;
                                                    var pos = (_b = (_a = document).caretPositionFromPoint) === null || _b === void 0 ? void 0 : _b.call(_a, clickX_1, clickY_1);
                                                    if (!pos)
                                                        return null;
                                                    var r = document.createRange();
                                                    r.setStart(pos.offsetNode, pos.offset);
                                                    r.collapse(true);
                                                    return r;
                                                })();
                                                setTimeout(function () {
                                                    var _a;
                                                    (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                                    if (preRange_1) {
                                                        var sel = window.getSelection();
                                                        if (sel) {
                                                            sel.removeAllRanges();
                                                            sel.addRange(preRange_1);
                                                        }
                                                    }
                                                }, 0);
                                            }
                                            else if (!wasEditing) {
                                                // onFocus fired before onClick; isEditing state was already flushed to true.
                                                // Use wasEditingAtPointerRef to detect this first-focus case.
                                                // We cannot place cursor synchronously here because contentEditable is still
                                                // false (React re-render hasn't committed yet). Use setTimeout(0) so we run
                                                // AFTER React commits contentEditable=true — cursor placement will then stick.
                                                var x_1 = e.clientX;
                                                var y_1 = e.clientY;
                                                setTimeout(function () {
                                                    var _a, _b, _c, _d;
                                                    var editor = (_a = labelWrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.editorDiv');
                                                    if (!editor)
                                                        return;
                                                    // If focus moved away before timeout fired, don't interfere
                                                    if (!((_b = labelWrapperRef.current) === null || _b === void 0 ? void 0 : _b.contains(document.activeElement))) {
                                                        editor.focus({ preventScroll: true });
                                                    }
                                                    var range = (_d = (_c = document.caretRangeFromPoint) === null || _c === void 0 ? void 0 : _c.call(document, x_1, y_1)) !== null && _d !== void 0 ? _d : (function () {
                                                        var _a, _b;
                                                        var pos = (_b = (_a = document).caretPositionFromPoint) === null || _b === void 0 ? void 0 : _b.call(_a, x_1, y_1);
                                                        if (!pos)
                                                            return null;
                                                        var r = document.createRange();
                                                        r.setStart(pos.offsetNode, pos.offset);
                                                        r.collapse(true);
                                                        return r;
                                                    })();
                                                    if (range) {
                                                        var sel = window.getSelection();
                                                        if (sel) {
                                                            sel.removeAllRanges();
                                                            sel.addRange(range);
                                                        }
                                                    }
                                                }, 0);
                                            }
                                        }
                                    }, children: _jsx(FractionTileMathEditor, { ref: mathEditorRef, data: {
                                            type: 'data',
                                            data: {
                                                text: customLabel
                                                    ? Array.isArray(customLabel)
                                                        ? customLabel
                                                        : [{ type: 'text', value: customLabel }]
                                                    : [],
                                                color: color === 'black' || color === '#000000' || color === '#232323'
                                                    ? 'white'
                                                    : '#293273',
                                                alignment: 'center',
                                                size: 20 * tileScale,
                                            },
                                        }, className: "fraction-tile-manipulative-label-input", isTextUpdate: false, 
                                        // Anchor the keypad to the tile, not to the label's editorDiv:
                                        // that box is width:fit-content and centred in the tile, so a
                                        // keypad a gap away from *it* lands inside the tile and the
                                        // label slides under it as the user types — worst with a
                                        // fraction, which widens the box in one step.
                                        keypadAnchorRef: tileInteractiveRef, idPreffix: "".concat(id, "_custom_label_rect"), index: 1, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, blockNewLine: true, throwChangeOnBlur: true, maxCharacters: maxChars, setActiveMathText: setMathKeyboardActive, viewonly: disabled || selectedTool.length > 1 || editingLock, cb: function (val) {
                                            var currentLabelStr = JSON.stringify(customLabel);
                                            var newLabelStr = JSON.stringify(val);
                                            if (currentLabelStr !== newLabelStr) {
                                                handleMathEditorChange(val);
                                            }
                                        }, dataPlaceholder: isFocused ? t('text.typeHere', { defaultValue: 'Type here' }) : '​' }, "".concat(id, "_custom_label_rect")) })) : (_jsx("span", { className: "fraction-tile-manipulative-label ".concat(lablesType === 'custom' && !labelTitle ? 'is-empty' : ''), style: {
                                        '--label-font-size': !!FRACTION_CONFIG[fractionSize]
                                            ? "".concat(1.5625 * tileScale, "rem")
                                            : "".concat((1 +
                                                0.5625 *
                                                    Math.min(1, Math.max(0, (tileSize.width / tileScale - 55) / (165 - 55)))) *
                                                tileScale, "rem"),
                                        '--label-color': color === 'black' || color === '#000000' || color === '#232323'
                                            ? 'white'
                                            : '#293273',
                                    }, children: labelTitle }))), lablesType === 'custom' &&
                                !isSmallTile &&
                                isEditing &&
                                isFocused &&
                                selectedTool.length <= 1 &&
                                !disabled &&
                                !editingLock && (_jsx(ButtonComponent, { type: "imageType5", imgSrc: __assign({}, btnConfig.mathtext.imgSrc), defaultCSS: "fraction-tile-math-btn", clickActive: true, isActive: mathKeyboardActive === 1, controlActiveFromOutside: true, ariaLabel: t('text.mathText'), title: t('text.mathText'), 
                                // This button opens the math keypad toolbar, so it must say it
                                // controls a popup and whether that popup is currently open —
                                // it exposed neither. Same signal the active styling uses.
                                ariaHaspopup: true, ariaExpanded: mathKeyboardActive === 1, onTouchStart: function (e) {
                                    // iPad: suppress iOS mouse-event synthesis (mousedown/mouseup/click)
                                    // so the freshly-inserted math-field never blurs to a synthesized
                                    // click target. Parent's onTouchEnd already drives insertMath()
                                    // directly for this button.
                                    e.preventDefault();
                                }, onMouseDown: function (e) {
                                    // Desktop: prevent the button from stealing focus from the
                                    // editorDiv on mousedown. (iPad's mouse events are now
                                    // suppressed by the onTouchStart preventDefault above.)
                                    e.preventDefault();
                                }, clickHandler: function () { var _a; return (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.insertMath(); }, showToolTip: true, direction: "bottom", isWhiteBoard: true, isParentRelative: true }))] })) : (_jsxs("div", { className: "fraction-tile-manipulative-circle-container", style: {
                            width: bb.width * tileScale,
                            height: bb.height * tileScale,
                            pointerEvents: 'none',
                            position: 'relative',
                        }, children: [_jsx("div", { className: "fraction-tile-manipulative-shapes-outer", style: {
                                    '--tile-clip-path': clipPath,
                                }, children: _jsxs("svg", { width: "100%", height: "100%", viewBox: "".concat(bb.x, " ").concat(bb.y, " ").concat(bb.width, " ").concat(bb.height), className: "fraction-tile-manipulative-svg", style: { pointerEvents: 'auto' }, children: [_jsx("rect", { x: bb.x, y: bb.y, width: bb.width, height: bb.height, fill: "transparent", style: { pointerEvents: 'all' } }), renderCircleShape()] }) }), circleLabelOverlay && (_jsx("div", { className: "fraction-tile-manipulative-circle-label-overlay", style: {
                                    '--label-x': "".concat(circleLabelOverlay.labelXPct, "%"),
                                    '--label-y': "".concat(circleLabelOverlay.labelYPct, "%"),
                                    '--label-width': "".concat(circleLabelOverlay.labelWidthPct, "%"),
                                    '--label-rotation': "".concat(circleLabelOverlay.finalRotation, "deg"),
                                }, children: lablesType === 'custom' && !isSmallTile ? (_jsx("div", { ref: labelWrapperCircleRef, className: "fraction-tile-manipulative-label-input-wrapper", style: {
                                        '--tile-height': "".concat(bb.height * tileScale, "px"),
                                        '--tile-scale': tileScale,
                                        pointerEvents: editingLock ? 'none' : undefined,
                                    }, onMouseDownCapture: onMouseDown, onTouchStartCapture: onMouseDown, onPointerDownCapture: function (e) {
                                        var _a, _b;
                                        var alreadyFocused = !!((_a = labelWrapperCircleRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement));
                                        wasEditingAtPointerRef.current = isEditing;
                                        var target = e.target;
                                        var isMathFieldClick = target.tagName === 'MATH-FIELD' || !!((_b = target.closest) === null || _b === void 0 ? void 0 : _b.call(target, 'math-field'));
                                        if (isEditing && alreadyFocused && !isMathFieldClick) {
                                            e.stopPropagation();
                                        }
                                    }, onFocus: function () {
                                        if (!editingLock) {
                                            if (!pointerDownRef.current) {
                                                setIsEditing(true);
                                                if (selectedTool.length > 1) {
                                                    setSelectedTool(["".concat(id, "_custom_label_circle_1")]);
                                                }
                                            }
                                            else if (!lastFocusWasScreenReader.current) {
                                                setIsEditing(true);
                                                if (selectedTool.length > 1) {
                                                    setSelectedTool(["".concat(id, "_custom_label_circle_1")]);
                                                }
                                            }
                                            if (!isFocused) {
                                                setSelectedTool(["".concat(id, "_custom_label_circle_1")]);
                                            }
                                        }
                                        if (!pointerDownRef.current) {
                                            voiceFocusRef.current = true;
                                        }
                                    }, onKeyDown: function (e) {
                                        var _a;
                                        if (e.key !== 'Tab' || e.shiftKey)
                                            return;
                                        var mathBtn = (_a = tileInteractiveRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.fraction-tile-math-btn');
                                        if (!mathBtn)
                                            return;
                                        e.preventDefault();
                                        mathBtn.focus();
                                    }, onClick: function (e) {
                                        var _a, _b, _c;
                                        if (suppressNextLabelClick.current) {
                                            suppressNextLabelClick.current = false;
                                            return;
                                        }
                                        if (IS_TOUCH_DEVICE && lastFocusWasScreenReader.current)
                                            return;
                                        if (voiceFocusRef.current)
                                            return;
                                        var target = e.target;
                                        if (target.closest('.keypadWrapper') ||
                                            target.closest('.fraction-tile-math-btn'))
                                            return;
                                        var alreadyFocused = !!((_a = labelWrapperCircleRef.current) === null || _a === void 0 ? void 0 : _a.contains(document.activeElement));
                                        if (!editingLock && !disabled) {
                                            setIsEditing(true);
                                            var wasEditing = wasEditingAtPointerRef.current;
                                            if (!alreadyFocused) {
                                                var clickX_2 = e.clientX;
                                                var clickY_2 = e.clientY;
                                                var preRange_2 = (_c = (_b = document.caretRangeFromPoint) === null || _b === void 0 ? void 0 : _b.call(document, clickX_2, clickY_2)) !== null && _c !== void 0 ? _c : (function () {
                                                    var _a, _b;
                                                    var pos = (_b = (_a = document).caretPositionFromPoint) === null || _b === void 0 ? void 0 : _b.call(_a, clickX_2, clickY_2);
                                                    if (!pos)
                                                        return null;
                                                    var r = document.createRange();
                                                    r.setStart(pos.offsetNode, pos.offset);
                                                    r.collapse(true);
                                                    return r;
                                                })();
                                                setTimeout(function () {
                                                    var _a;
                                                    (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                                                    if (preRange_2) {
                                                        var sel = window.getSelection();
                                                        if (sel) {
                                                            sel.removeAllRanges();
                                                            sel.addRange(preRange_2);
                                                        }
                                                    }
                                                }, 0);
                                            }
                                            else if (!wasEditing) {
                                                var x_2 = e.clientX;
                                                var y_2 = e.clientY;
                                                setTimeout(function () {
                                                    var _a, _b, _c, _d;
                                                    var editor = (_a = labelWrapperCircleRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.editorDiv');
                                                    if (!editor)
                                                        return;
                                                    if (!((_b = labelWrapperCircleRef.current) === null || _b === void 0 ? void 0 : _b.contains(document.activeElement))) {
                                                        editor.focus({ preventScroll: true });
                                                    }
                                                    var range = (_d = (_c = document.caretRangeFromPoint) === null || _c === void 0 ? void 0 : _c.call(document, x_2, y_2)) !== null && _d !== void 0 ? _d : (function () {
                                                        var _a, _b;
                                                        var pos = (_b = (_a = document).caretPositionFromPoint) === null || _b === void 0 ? void 0 : _b.call(_a, x_2, y_2);
                                                        if (!pos)
                                                            return null;
                                                        var r = document.createRange();
                                                        r.setStart(pos.offsetNode, pos.offset);
                                                        r.collapse(true);
                                                        return r;
                                                    })();
                                                    if (range) {
                                                        var sel = window.getSelection();
                                                        if (sel) {
                                                            sel.removeAllRanges();
                                                            sel.addRange(range);
                                                        }
                                                    }
                                                }, 0);
                                            }
                                        }
                                    }, children: _jsx(FractionTileMathEditor, { ref: mathEditorRef, data: {
                                            type: 'data',
                                            data: {
                                                text: customLabel
                                                    ? Array.isArray(customLabel)
                                                        ? customLabel
                                                        : [{ type: 'text', value: customLabel }]
                                                    : [],
                                                color: color === 'black' || color === '#000000' || color === '#232323'
                                                    ? 'white'
                                                    : '#293273',
                                                alignment: 'center',
                                                size: 20 * tileScale,
                                            },
                                        }, className: "fraction-tile-manipulative-label-input", isTextUpdate: false, 
                                        // Anchor the keypad to the tile, not to the label's editorDiv:
                                        // that box is width:fit-content and centred in the tile, so a
                                        // keypad a gap away from *it* lands inside the tile and the
                                        // label slides under it as the user types — worst with a
                                        // fraction, which widens the box in one step.
                                        keypadAnchorRef: tileInteractiveRef, idPreffix: "".concat(id, "_custom_label_circle"), index: 1, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, blockNewLine: true, throwChangeOnBlur: true, maxCharacters: maxChars, setActiveMathText: setMathKeyboardActive, viewonly: disabled || selectedTool.length > 1 || editingLock, cb: function (val) {
                                            var currentLabelStr = JSON.stringify(customLabel);
                                            var newLabelStr = JSON.stringify(val);
                                            if (currentLabelStr !== newLabelStr) {
                                                handleMathEditorChange(val);
                                            }
                                        }, dataPlaceholder: isFocused ? t('text.typeHere', { defaultValue: 'Type here' }) : '​' }, "".concat(id, "_custom_label_circle")) })) : (_jsx("span", { className: "fraction-tile-manipulative-label ".concat(lablesType === 'custom' && !labelTitle ? 'is-empty' : ''), style: {
                                        pointerEvents: 'none',
                                        '--label-color': color === 'black' ? 'white' : '#293273',
                                        '--label-font-size': circleLabelOverlay.fontSize,
                                    }, children: labelTitle })) })), lablesType === 'custom' &&
                                !isSmallTile &&
                                isEditing &&
                                isFocused &&
                                selectedTool.length <= 1 &&
                                !disabled &&
                                !editingLock && (_jsx(ButtonComponent, { type: "imageType5", imgSrc: __assign({}, btnConfig.mathtext.imgSrc), defaultCSS: "fraction-tile-math-btn", clickActive: true, isActive: mathKeyboardActive === 1, controlActiveFromOutside: true, ariaLabel: t('text.mathText'), title: t('text.mathText'), 
                                // This button opens the math keypad toolbar, so it must say it
                                // controls a popup and whether that popup is currently open —
                                // it exposed neither. Same signal the active styling uses.
                                ariaHaspopup: true, ariaExpanded: mathKeyboardActive === 1, onTouchStart: function (e) {
                                    // iPad: suppress iOS mouse-event synthesis (mousedown/mouseup/click)
                                    // so the freshly-inserted math-field never blurs to a synthesized
                                    // click target. Parent's onTouchEnd already drives insertMath()
                                    // directly for this button.
                                    e.preventDefault();
                                }, onMouseDown: function (e) {
                                    // Desktop: prevent the button from stealing focus from the
                                    // editorDiv on mousedown. (iPad's mouse events are now
                                    // suppressed by the onTouchStart preventDefault above.)
                                    e.preventDefault();
                                }, clickHandler: function () { var _a; return (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.insertMath(); }, showToolTip: true, direction: "bottom", isWhiteBoard: true, isParentRelative: true }))] })), isFocused && (_jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            padding: 0,
                            margin: '-1px',
                            overflow: 'hidden',
                            clip: 'rect(0, 0, 0, 0)',
                            whiteSpace: 'nowrap',
                            border: 0,
                        }, children: announceText }))] })] }));
};
export default FractionTileManipulative;
